import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  Inject,
  PLATFORM_ID
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Location } from '@angular/common';
import { TimelineDataProviderService, TimelinePageResult } from './timeline.data-provider.service';
import { PostViewModel } from './post.view-model';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormGroup } from '@angular/forms';
import { RxFormBuilder, RxwebValidators } from '@rxweb/reactive-form-validators';
import { LoaderService } from '../../core/services/loader.service';
import { ProjectViewModel } from '../../features/user/garage/project.view-model';
import { CommentApiModel } from '../../api/models/comment.api.model';
import { ContextMenuObjectInterface } from './context-menu-object.interface';
import { MediaObjectInterface } from '../../core/interfaces/media-object.interface';
import { TimelineGridTileClickAction, TimelineTileComponent } from './timeline-tile/timeline-tile.component';
import { PostApiModel } from '../../api/models/post-api.model';
import { EventSourceService } from '../../core/services/event-source.service';
import { finalize, map, tap } from 'rxjs/operators';
import { TokenDataProviderService } from '../../core/services/token-data-provider.service';
import { CurrentUserViewModel } from '../../core/models/current-user.view-model';
import { EventSourceInterface } from '../../core/interfaces/event-source.interface';
import {PostProcessingEventInterface} from "../../core/interfaces/post-processing-event.interface";
import { InputModeTypes } from './timeline-action-panel/timeline-action-panel.component';
import { PostTypeEnum } from '../../core/enums/post-type.enum';
import { SocialInputSubmitInterface } from '../social/social-input/social-input-submit.interface';
import { EventApiModel } from '../../api/models/event.api.model';
import { VideoService } from './video.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CacheRefreshService } from '../../core/services/cache-refresh.service';
import { PullToRefreshDirective } from '../directives/pull-to-refresh.directive';
import { MediaTypeEnum } from '../../core/enums/media-type.enum';
import { ActionsService } from '../footer/actions.service';
import { ViewTypeEnum } from '../../core/enums/view-type.enum';
import { AbstractComponent } from "../../core/components/abstract-component";
import { ModalService } from "../modal/modal.service";
import { ViolationTypeEnum } from "../../core/enums/violation-type.enum";
import { AbstractApiModel } from "../../api/models/abstract.api.model";
import { ResponseNotificationService } from "../../core/services/response-notification.service";
import { Injector } from "@angular/core";
import {BottomSheetComponent} from "../components/bottom-sheet/bottom-sheet.component";
import {SocialActionsComponent} from '../social/social-actions/social-actions.component';
import {MediaOfflineService} from "../../core/services/media-offline.service";
import {MediaObjectPipe} from "../../core/pipes/media-object.pipe";
import {MediaObjectRestService} from "../../api/services/media-object.rest.service";
import {plainToClass} from 'class-transformer';
import { BehaviorSubject, Observable } from 'rxjs';

interface TimelineVirtualItem {
  index: number;
  key: string;
  post: PostViewModel;
}

interface TimelineVirtualGridRow {
  items: TimelineVirtualItem[];
  key: string;
  rowIndex: number;
}

export interface TimelineGridColumnsConfig {
  default: number;
  tablet?: number;
  desktop?: number;
}

/**
 * Swipe previews render the regular timeline tree with existing data only.
 * They deliberately omit every live data source and media-player lifecycle.
 */
export type TimelineRenderMode = 'active' | 'swipe-preview';

/**
 * Lightweight, serialisable-in-memory state used when a host temporarily
 * destroys a timeline. It deliberately contains post models only: media DOM,
 * HLS and Plyr instances always belong to the live component instance.
 */
export interface TimelineStateSnapshot {
  records: PostViewModel[];
  page: number;
  total: number;
  endOfResults: boolean;
  scrollY: number;
  ready: boolean;
}

@Component({
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],

})
export class TimelineComponent extends AbstractComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  private readonly appendPlaceholderMarker = '__appendPlaceholder';
  private static activeInstances = 0;
  get permissions(): string[] {
    return this._permissions;
  }

  @Input() set permissions(value: string[]) {
    this._permissions = value;
  }

  get postTypes(): string[] {
    return this._postTypes;
  }

  @Input() set postTypes(value: string[]) {
    this._postTypes = value;
  }

  get viewType(): ViewTypeEnum {
    return this._viewType;
  }

  @Input() set viewType(value: ViewTypeEnum) {
    this._viewType = value;
    this.scheduleGridSync(`view-type:${value}`, `view-type:${value}`);
  }

  get items(): MenuItem[] {
    return this._items;
  }

  set items(value: MenuItem[]) {
    this._items = value;
  }

  constructor(
    private readonly timelineDPS: TimelineDataProviderService,
    private readonly translate: TranslateService,
    private readonly confirmationService: ConfirmationService,
    private readonly fb: RxFormBuilder,
    private readonly ls: LoaderService,
    private readonly eventSource: EventSourceService,
    private readonly cdr: ChangeDetectorRef,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly videoService: VideoService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly actionsService: ActionsService,
    private readonly modalService: ModalService,
    private readonly cacheRefresh: CacheRefreshService,
    private readonly mediaOffline: MediaOfflineService,
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly injector: Injector,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private platformId: Object

  ) {
    super();
  }

  @Input() writingPostEnabled = true;
  @Input() renderMode: TimelineRenderMode = 'active';
  /** Enables thumbnail sources only for swipe-preview panels currently entering the viewport. */
  @Input() previewMediaEnabled = true;
  /** Parent-owned user snapshot used only to render the preview composer. */
  @Input() previewCurrentUser: CurrentUserViewModel | null = null;
  @Input() fetchDeleted = false;
  @Input() viewSwitchEnabled = false;
  /**
   * Keeps the historical timeline behaviour by default. Embedded timelines can
   * opt out so a page-level query parameter cannot change their local view.
   */
  @Input() syncViewWithUrl = true;
  @Input() gridTileClickAction: TimelineGridTileClickAction = 'navigate-to-list';
  /** Home Media may warm HLS manifests for video tiles mounted by its virtual viewport. */
  @Input() gridGalleryVideoPrewarmEnabled = false;
  @Input() pullToRefreshEnabled = true;
  @Input() prefetchEnabled = true;
  /** Disables only cache/network warming; pagination remains available. */
  @Input() mediaPrewarmEnabled = true;
  /** Renders grid placeholders without starting image requests. */
  @Input() deferGridMedia = false;
  @Input() mediaType: MediaTypeEnum | null = null;
  /** Limits media shown by tiles and their gallery without changing API filtering. */
  @Input() visibleMediaType: MediaTypeEnum | null = null;
  /** Optional live gallery context for an embedded paginated sequence. */
  @Input() galleryPostSequence$?: Observable<PostViewModel[]>;
  @Input() galleryLoadNextPostPage?: () => Promise<boolean>;
  @Input() galleryHasNextPostPage?: () => boolean;
  @Input() galleryPostPageSize?: number;
  @Input() galleryPrefetchAtPostIndex?: number;
  private _permissions: string[] = [];
  private _postTypes: string[] = [];
  private _viewType: ViewTypeEnum = ViewTypeEnum.LIST;
  @Input() projectVM: ProjectViewModel;
  @Input() postAsPostType: PostTypeEnum;
  @Input() eventAM: EventApiModel;
  @Input() startIndex: number;
  @Input() noResultsMsg: string;
  @Input() alreadyOnRecentMsg: string;
  /** First page fetched by a parent view before this timeline is mounted. */
  @Input() preloadedPage: TimelinePageResult | null = null;
  /**
   * Lets a parent mount the timeline immediately while its shared first-page
   * request is still pending. This keeps placeholders visible without issuing
   * a second request from the child.
   */
  @Input() awaitPreloadedPage = false;
  /**
   * Restores a previously emitted lightweight state. The input is consumed
   * only during initialisation so a host can safely retain newer snapshots
   * without resetting a currently visible timeline.
   */
  @Input() restoredState: TimelineStateSnapshot | null = null;
  /**
   * Home swipe preview already renders one of these inputs. Preserve its
   * records and virtual keys while promoting it to the active timeline.
   */
  private previewSourcePage: TimelinePageResult | null = null;
  private previewSourceState: TimelineStateSnapshot | null = null;
  private _gridColumns: number | TimelineGridColumnsConfig = 3;

  @Input() set gridColumns(value: number | TimelineGridColumnsConfig) {
    this._gridColumns = value || 3;
    this.effectiveGridColumns = this.resolveEffectiveGridColumns();
    this.scheduleVirtualLayoutSync();
  }

  get gridColumns(): number | TimelineGridColumnsConfig {
    return this._gridColumns;
  }
  @Output() viewTypeChange: EventEmitter<ViewTypeEnum> = new EventEmitter<ViewTypeEnum>();
  /** Emits only real posts, never initial or append-loading placeholders. */
  @Output() loadedPostsChange: EventEmitter<PostViewModel[]> = new EventEmitter<PostViewModel[]>();
  @Output() timelineStateChange: EventEmitter<TimelineStateSnapshot> = new EventEmitter<TimelineStateSnapshot>();
  /** Scroll is intentionally emitted separately so a scroll never clones the full post sequence. */
  @Output() timelineScrollChange: EventEmitter<number> = new EventEmitter<number>();
  /** Replayable source consumed by galleries opened from this timeline. */
  public readonly loadedPosts$ = new BehaviorSubject<PostViewModel[]>([]);
  public readonly loadNextPageForGallery = (): Promise<boolean> => this.loadNextPage();
  public readonly hasNextPageForGallery = (): boolean => this.canLoadNextPage;
  @ViewChild('timelineViewport', { static: false }) timelineViewport?: ElementRef<HTMLElement>;
  @ViewChild('appendSentinel', { static: false }) appendSentinel?: ElementRef<HTMLElement>;
  @ViewChild('timelineCommentsBottomSheet') timelineCommentsBottomSheet?: BottomSheetComponent;
  @ViewChild('timelineActionBottomSheet') timelineActionBottomSheet?: BottomSheetComponent;
  @ViewChild(SocialActionsComponent) private mobileSocialActions?: SocialActionsComponent;
  @ViewChild('commentEditBody') private commentEditBody?: ElementRef<HTMLElement>;
  @ViewChildren('virtualItemRef', { read: ElementRef }) private virtualItemRefs?: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren(TimelineTileComponent) private timelineTileComponents: QueryList<TimelineTileComponent>;

  public page = 2;
  public endOfResults = false;
  public totalItems = 0;
  public actionSheetMounted = false;
  public sidebarAction: InputModeTypes;
  private commentEditMiddleHeight: number | null = null;
  private commentEditResizeObserver: ResizeObserver | null = null;
  private commentEditResizeFrame: number | null = null;
  public form: UntypedFormGroup;
  public placeholders: PostViewModel[] = Array.from({ length: 9 }, (value, key) => new PostViewModel());
  @Input() public posts: PostViewModel[] = this.placeholders;
  private loadedPosts: PostViewModel[] = [];
  @Input() public singlePostMode: boolean = false;
  public contextMenuObject: ContextMenuObjectInterface<PostViewModel | CommentApiModel> = null;
  private _items: MenuItem[];
  public currentUser: CurrentUserViewModel;
  public mobileCommentsPost: PostViewModel | null = null;
  public mobileCommentsSheetMounted: boolean = false;
  public spinnerState = false;
  public submitDisabled = false;
  public isPlaying = false;
  public isRendered = false;
  public status: any;
  public isRefreshing = false;
  public isViewSwitching = false;
  private readonly gridScrollStateKey = 'timelineGridScrollY';
  public virtualVisibleItems: TimelineVirtualItem[] = [];
  public virtualVisibleGridRows: TimelineVirtualGridRow[] = [];
  public virtualTopSpacerHeight = 0;
  public virtualBottomSpacerHeight = 0;
  public effectiveGridColumns = 3;

  public readonly MediaTypeEnum = MediaTypeEnum;

  public readonly ViewTypeEnum = ViewTypeEnum;
  private processingVersionsByPostId = new Map<number, number>();
  private processingStatusByPostId = new Map<number, string>();
  private recentProcessingEventKeys = new Map<string, number>();
  private backendThumbnailRefreshInFlightByMediaId = new Set<number>();
  private backendThumbnailPromoteInFlightByPostId = new Set<number>();
  private inFlightOwnedPostIds = new Set<number>();
  private localGridThumbnailByPostId = new Map<number, string>();
  private backendGridThumbnailReadyByPostId = new Set<number>();
  private activeProcessingTopics: string[] = [];
  private feedContextKey: string | null = null;
  private feedContextVersion = 0;
  private hasFeedInitialized = false;
  private gridSyncScheduled = false;
  private pendingGridSyncReasons: string[] = [];
  private pendingGridSyncCallbacks: Array<() => void> = [];
  private pendingGridSyncStartAt: number | null = null;
  private pendingGridSyncInteraction: string | null = null;
  private performanceObserver: PerformanceObserver | null = null;
  private layoutShiftCount = 0;
  private layoutShiftTotal = 0;
  private isPostsPageLoading = false;
  private nextPageLoadPromise: Promise<boolean> | null = null;
  private readonly virtualListItemHeights = new Map<string, number>();
  private readonly observedVirtualElements = new Map<string, HTMLElement>();
  private virtualResizeObserver: ResizeObserver | null = null;
  private virtualAppendObserver: IntersectionObserver | null = null;
  private observedAppendSentinel: HTMLElement | null = null;
  private virtualOffsets: number[] = [0];
  private virtualAverageListItemHeight = 520;
  private virtualGridCellSize = 0;
  private virtualGridRowStride = 0;
  private virtualLayoutScheduled = false;
  private pendingPreviewTargetId: string | null = null;
  private pendingPreviewIndex: number | null = null;
  private readonly virtualGridGapPx = 4;
  private readonly virtualMinListRenderCount = 6;
  private readonly virtualFallbackListHeight = 520;
  private readonly appendTriggerRootMarginPx = 900;
  /** Keep one small viewport warm; MediaOfflineService bounds global I/O. */
  private readonly prewarmWindowPostCount = 12;
  private static prewarmScopeSequence = 0;
  private readonly mediaPrewarmScope = `timeline-${TimelineComponent.prewarmScopeSequence++}`;
  /** Bare HLS decoders for videos in the real, on-screen Media viewport. */
  /** Shared with grid tiles only to claim their currently visible warm decoder. */
  public readonly gridViewportDecoderScope = `${this.mediaPrewarmScope}:grid-decoders`;
  private readonly gridGalleryIntentPrewarmScope = `${this.mediaPrewarmScope}:grid-gallery-intent`;
  private prewarmTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private gridGalleryIntentPrewarmTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private stateSnapshotTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private scrollStateTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private registeredAsActiveInstance = false;
  private readonly previewFallbackListItemCount = 3;
  private readonly previewFallbackGridRowCount = 4;

  public get isSwipePreview(): boolean {
    return this.renderMode === 'swipe-preview';
  }

  @HostListener('window:load', ['$event'])
  @HostListener('window:scroll', ['$event'])
  videoScroll() {
    if (this.isSwipePreview) {
      return;
    }
    this.videoService.videoScroll();
    this.scheduleVirtualLayoutSync();
    this.scheduleTimelineScrollEmit();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isSwipePreview) {
      return;
    }
    this.scheduleVirtualLayoutSync();
  }

  @HostListener('window:popstate')
  onPopState(): void {
    if (this.isSwipePreview || !this.syncViewWithUrl) {
      return;
    }
    this.applyViewFromQuery(this.getViewQueryFromCurrentUrl());
  }

  ngAfterViewInit() {
    if (this.isSwipePreview) {
      // The first pass uses an SSR-safe fallback. Once the normal timeline
      // container exists, calculate one static viewport-filling slice on the
      // next frame. Updating the grid synchronously here changes ngFor after
      // Angular's verification pass and raises ExpressionChanged errors.
      this.scheduleSwipePreviewViewportRender();
      return;
    }
    if (this.virtualItemRefs) {
      this.subscriptions.push(this.virtualItemRefs.changes.subscribe(() => {
        this.syncVirtualItemObservers();
      }));
    }
    this.runOnNextFrame(() => {
      this.syncVirtualItemObservers();
      this.syncAppendSentinelObserver();
      this.scheduleVirtualLayoutSync();
    });
  }

  ngOnDestroy() {
    this.stopCommentEditSizing();
    if (!this.isSwipePreview) {
      this.emitTimelineState();
    }
    super.ngOnDestroy();
    // Angular owns the QueryList lifecycle. Destroying it manually while its
    // containing LView is torn down can leave stale query matches retained.
    this.closeProcessingEventTopics();
    // EventSourceService owns shared /posts and /comments streams. Closing all
    // sources here used to be safe only because Home kept every tab mounted;
    // with per-tab teardown it would disconnect unrelated subscribers on each
    // pill switch. Component subscriptions are released by AbstractComponent.
    this.isRendered = false;
    this.disconnectVirtualObservers();
    this.performanceObserver?.disconnect();
    this.clearMediaPrewarm();
    if (this.stateSnapshotTimeoutId !== null) {
      clearTimeout(this.stateSnapshotTimeoutId);
      this.stateSnapshotTimeoutId = null;
    }
    if (this.scrollStateTimeoutId !== null) {
      clearTimeout(this.scrollStateTimeoutId);
      this.scrollStateTimeoutId = null;
    }
    if (typeof cancelAnimationFrame === 'function') {
      this.animationFrameIds.forEach((frameId) => cancelAnimationFrame(frameId));
    }
    this.animationFrameIds.length = 0;
    // Home already owns the captured TimelineStateSnapshot.  Do not leave the
    // destroyed virtual viewport holding its post graph when an asynchronous
    // browser listener temporarily retains this component's LView.
    this.releaseVirtualViewportReferences();
    this.loadedPosts$.next([]);
    this.loadedPosts$.complete();
    if (this.registeredAsActiveInstance) {
      TimelineComponent.activeInstances = Math.max(0, TimelineComponent.activeInstances - 1);
    }
  }

  /**
   * Keep teardown local to the component: Home holds the persisted records
   * needed for scroll restoration, while this instance must not retain old
   * tiles/media through its virtual viewport after Angular removes it.
   */
  private releaseVirtualViewportReferences(): void {
    this.virtualVisibleItems = [];
    this.virtualVisibleGridRows = [];
    this.virtualTopSpacerHeight = 0;
    this.virtualBottomSpacerHeight = 0;
    this.posts = [];
    this.loadedPosts = [];
    this.pendingPreviewTargetId = null;
    this.pendingPreviewIndex = null;
    this.mobileCommentsPost = null;
    this.mobileCommentsSheetMounted = false;
    this.processingVersionsByPostId.clear();
    this.processingStatusByPostId.clear();
    this.recentProcessingEventKeys.clear();
    this.backendThumbnailRefreshInFlightByMediaId.clear();
    this.backendThumbnailPromoteInFlightByPostId.clear();
    this.inFlightOwnedPostIds.clear();
    this.localGridThumbnailByPostId.clear();
    this.backendGridThumbnailReadyByPostId.clear();
    this.virtualListItemHeights.clear();
    this.observedVirtualElements.clear();
    this.virtualOffsets = [0];
    this.pendingGridSyncReasons = [];
    this.pendingGridSyncCallbacks = [];
    this.pendingGridSyncStartAt = null;
    this.pendingGridSyncInteraction = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['renderMode']
      && !changes['renderMode'].firstChange
      && changes['renderMode'].previousValue === 'swipe-preview'
      && this.renderMode === 'active'
    ) {
      this.activateFromSwipePreview();
      return;
    }
    if (this.isSwipePreview) {
      if (
        (changes['preloadedPage'] && !changes['preloadedPage'].firstChange)
        || (changes['restoredState'] && !changes['restoredState'].firstChange)
      ) {
        this.initializeSwipePreview();
      }
      return;
    }
    if (changes['projectVM'] || changes['eventAM'] || changes['postTypes'] || changes['mediaType']) {
      this.refreshFeedForContextChange('input-change');
    }

    if ((changes['prefetchEnabled'] || changes['mediaPrewarmEnabled']) && (!this.prefetchEnabled || !this.mediaPrewarmEnabled)) {
      this.clearMediaPrewarm();
    } else if ((changes['prefetchEnabled'] || changes['mediaPrewarmEnabled']) && this.prefetchEnabled && this.mediaPrewarmEnabled) {
      this.schedulePrewarmMedia([], 'media-prewarm-enabled');
    }

    if (
      changes['preloadedPage']
      && this.hasFeedInitialized
      && !this.singlePostMode
      && this.preloadedPage
      && this.preloadedPage !== this.previewSourcePage
    ) {
      this.applyPreloadedFirstPage(this.preloadedPage);
    }

    if (
      changes['awaitPreloadedPage']
      && this.hasFeedInitialized
      && !this.singlePostMode
      && !this.awaitPreloadedPage
      && !this.preloadedPage
      && !this.isPostsPageLoading
      && this.hasPlaceholderPosts()
    ) {
      this.loadPostsPage(1);
    }
  }

  ngOnInit(): void {
    if (this.isSwipePreview) {
      this.initializeSwipePreview();
      return;
    }

    this.initializeActiveTimeline();
  }

  /**
   * Promotes an already-rendered Home swipe preview instead of replacing its
   * component instance. The preview tree stays in place while the normal
   * timeline lifecycle is attached exactly once.
   */
  private activateFromSwipePreview(): void {
    this.initializeActiveTimeline();
    if (this.virtualItemRefs) {
      this.subscriptions.push(this.virtualItemRefs.changes.subscribe(() => {
        this.syncVirtualItemObservers();
      }));
    }
    this.runOnNextFrame(() => {
      this.syncVirtualItemObservers();
      this.syncAppendSentinelObserver();
      this.scheduleVirtualLayoutSync();
    });
  }

  private initializeActiveTimeline(): void {
    if (this.registeredAsActiveInstance) {
      return;
    }

    TimelineComponent.activeInstances++;
    this.registeredAsActiveInstance = true;
    this.createForm();
    this.endOfResults = false;

    if (this.syncViewWithUrl) {
      this.applyViewFromQuery(this.getViewQueryFromCurrentUrl());
    }
    this.registerPerformanceDiagnostics();
    if (this.syncViewWithUrl) {
      this.subscriptions.push(
        this.route.queryParams.subscribe((params) => {
          this.applyViewFromQuery(params?.['view'] as ViewTypeEnum | undefined);
        })
      );
    }

    // BehaviorSubject emits immediately. Build the initial key first so this
    // initial emission does not reset a preloaded Home grid to placeholders.
    this.feedContextKey = this.buildFeedContextKey();
    this.subscriptions.push(this.tokenDPS.currentUser.subscribe((u: CurrentUserViewModel) => {
      this.currentUser = u;
      this.refreshFeedForContextChange('viewer-change');
      this.syncProcessingEventTopics();
    }));
    this.feedContextKey = this.buildFeedContextKey();

    this.hasFeedInitialized = true;
    if (!this.singlePostMode) {
      if (this.restoredState?.ready && this.restoredState !== this.previewSourceState) {
        this.applyRestoredState(this.restoredState);
      } else if (this.preloadedPage && this.preloadedPage !== this.previewSourcePage) {
        this.applyPreloadedFirstPage(this.preloadedPage);
      } else if (this.previewSourcePage || this.previewSourceState) {
        this.scheduleGridSync('swipe-preview-promoted', 'swipe-preview-promoted');
      } else if (!this.awaitPreloadedPage) {
        this.loadPostsPage(1);
      } else {
        this.scheduleGridSync('await-preloaded-page', 'await-preloaded-page');
      }
    }

    this.registerPostEventSource();
    this.registerCommentEventSource();
  }

  /**
   * A preview never owns state or a data source. It receives a small, static
   * slice from the host and renders the normal tile tree without observers,
   * EventSource subscriptions, pagination or media prewarming.
   */
  private initializeSwipePreview(): void {
    this.currentUser = this.previewCurrentUser || null;
    const usesRestoredState = !!this.restoredState?.ready;
    const records = usesRestoredState
      ? this.restoredState.records
      : this.preloadedPage?.records;

    this.previewSourceState = usesRestoredState ? this.restoredState : null;
    this.previewSourcePage = usesRestoredState ? null : this.preloadedPage;

    this.posts = records?.length ? [...records] : [];
    this.totalItems = records?.length ? (this.restoredState?.total || this.preloadedPage?.total || records.length) : 0;
    this.page = Math.max(2, this.restoredState?.page || (this.preloadedPage?.page || 1) + 1);
    this.endOfResults = !!this.restoredState?.endOfResults;
    this.renderSwipePreviewWindow();
  }

  private get previewItemLimit(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return this.viewType === ViewTypeEnum.GRID
        ? this.previewFallbackGridRowCount * this.resolveEffectiveGridColumns()
        : this.previewFallbackListItemCount;
    }

    const viewportHeight = this.getSwipePreviewViewportHeight();
    if (this.viewType !== ViewTypeEnum.GRID) {
      return Math.max(
        this.previewFallbackListItemCount,
        Math.ceil(viewportHeight / this.virtualFallbackListHeight) + 1
      );
    }

    const columns = this.resolveEffectiveGridColumns();
    const cellSize = this.getSwipePreviewGridCellSize(columns);
    const rowStride = Math.max(1, cellSize + this.virtualGridGapPx);
    const rows = Math.max(this.previewFallbackGridRowCount, Math.ceil(viewportHeight / rowStride) + 1);
    return rows * columns;
  }

  private renderSwipePreviewWindow(): void {
    const previewLimit = this.previewItemLimit;
    const previewPosts = (this.posts || []).slice(0, previewLimit);
    this.virtualTopSpacerHeight = 0;
    this.virtualBottomSpacerHeight = 0;

    if (this.viewType === ViewTypeEnum.GRID) {
      const columns = this.resolveEffectiveGridColumns();
      this.effectiveGridColumns = columns;
      this.virtualVisibleGridRows = Array.from({length: Math.ceil(previewPosts.length / columns)}, (_, rowIndex) => {
        const startIndex = rowIndex * columns;
        return {
          // Active virtualization must use the same key so Angular retains the
          // visible tile instances (and their loaded images) after promotion.
          key: `row:${rowIndex}`,
          rowIndex,
          items: previewPosts.slice(startIndex, startIndex + columns).map((post, offset) => ({
            post,
            index: startIndex + offset,
            key: this.getVirtualPostKey(post, startIndex + offset),
          })),
        };
      });
      this.virtualVisibleItems = [];
      return;
    }

    this.virtualVisibleItems = previewPosts.map((post, index) => ({
      post,
      index,
      key: this.getVirtualPostKey(post, index),
    }));
    this.virtualVisibleGridRows = [];
  }

  private scheduleSwipePreviewViewportRender(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const index = this.animationFrameIds.indexOf(frameId);
      if (index >= 0) {
        this.animationFrameIds.splice(index, 1);
      }
      if (!this.isSwipePreview) {
        return;
      }
      this.renderSwipePreviewWindow();
      this.cdr.detectChanges();
    });
    this.animationFrameIds.push(frameId);
  }

  private getSwipePreviewViewportHeight(): number {
    const viewportHeight = this.getViewportHeight();
    if (!viewportHeight) {
      return this.virtualFallbackListHeight * this.previewFallbackListItemCount;
    }

    const top = this.timelineViewport?.nativeElement?.getBoundingClientRect().top || 0;
    return Math.max(1, viewportHeight - Math.max(0, top));
  }

  private getSwipePreviewGridCellSize(columns: number): number {
    const viewportWidth = this.timelineViewport?.nativeElement?.clientWidth
      || this.document.documentElement?.clientWidth
      || (isPlatformBrowser(this.platformId) ? window.innerWidth : 0)
      || 0;
    const contentWidth = Math.max(0, viewportWidth - (this.virtualGridGapPx * (columns - 1)));
    return contentWidth > 0 ? contentWidth / columns : 120;
  }

  private refreshFeedForContextChange(reason: string): void {
    if (!this.hasFeedInitialized || this.singlePostMode) {
      return;
    }

    const nextContextKey = this.buildFeedContextKey();
    if (nextContextKey === this.feedContextKey) {
      return;
    }

    this.feedContextKey = nextContextKey;
    this.resetFeedStateForCurrentContext(reason);
    this.loadPostsPage(1);
  }

  private buildFeedContextKey(): string {
    const projectKey = this.resolveResourceKey(this.projectVM);
    const eventKey = this.resolveResourceKey(this.eventAM);
    const viewerKey = this.resolveResourceKey(this.currentUser);
    const postTypesKey = [...(this._postTypes || [])].sort().join('|');

    return [
      `project:${projectKey}`,
      `event:${eventKey}`,
      `viewer:${viewerKey}`,
      `types:${postTypesKey}`,
      `mediaType:${this.mediaType || 'none'}`,
    ].join(';');
  }

  private resolveResourceKey(resource: {id?: number | string; slug?: string; ['@id']?: string} | null | undefined): string {
    if (!resource) {
      return 'none';
    }

    const id = resource.id !== null && resource.id !== undefined ? String(resource.id).trim() : '';
    if (id) {
      return `id:${id}`;
    }

    const slug = resource.slug ? String(resource.slug).trim() : '';
    if (slug) {
      return `slug:${slug}`;
    }

    const iri = resource['@id'] ? String(resource['@id']).trim() : '';
    if (iri) {
      return `iri:${iri}`;
    }

    return 'none';
  }

  private resetFeedStateForCurrentContext(reason: string): void {
    this.feedContextVersion++;
    this.page = 2;
    this.endOfResults = false;
    this.totalItems = 0;
    this.isPostsPageLoading = false;
    this.isRefreshing = false;
    this.pendingPreviewTargetId = null;
    this.pendingPreviewIndex = null;
    this.mobileCommentsPost = null;
    this.mobileCommentsSheetMounted = false;
    this.closeProcessingEventTopics();
    this.processingVersionsByPostId.clear();
    this.processingStatusByPostId.clear();
    this.recentProcessingEventKeys.clear();
    this.backendThumbnailRefreshInFlightByMediaId.clear();
    this.backendThumbnailPromoteInFlightByPostId.clear();
    this.inFlightOwnedPostIds.clear();
    this.localGridThumbnailByPostId.clear();
    this.backendGridThumbnailReadyByPostId.clear();
    this.posts = [...this.placeholders];
    this.emitLoadedPosts();
    this.scheduleGridSync(`${reason}:placeholders`, `${reason}:placeholders`);
  }

  private closeProcessingEventTopics(): void {
    if (!this.activeProcessingTopics.length) {
      return;
    }

    this.eventSource.closeArrayOfTopics(this.activeProcessingTopics.map((topic) => `topic=${topic}`));
    this.activeProcessingTopics = [];
  }

  private initPostSlideMenu(postVM: PostViewModel): void {
    const isPostOwner = this.currentUser?.id != null && this.currentUser.id === postVM.postedBy?.id;
    this.items = [
      ...(isPostOwner ? [{
        label: this.translate.instant('common.update') + ' ' + this.translate.instant('common.post'),
        icon: 'pi pi-fw pi-pencil text-primary',
        command: () => this.openEditPostComposer(postVM)
      }] : []),
      ...(isPostOwner && postVM.type === PostTypeEnum.PROJECT_MEDIA_POST
      && !!postVM.media?.length
      && postVM.media[0]?.type === MediaTypeEnum.IMAGE ? [{
        label: (postVM.featuredAt ? this.translate.instant('common.unmark_as_featured') : this.translate.instant('common.mark_as_featured')) + ' ' + this.translate.instant('common.post'),
        icon: (postVM.featuredAt ? 'fa-solid fa-thumbtack-slash' : 'fa-solid fa-thumbtack') + ' text-primary',
        command: () => {
          this.ls.show();
          this.subscriptions.push(this.timelineDPS.togglePostFeatured(postVM).subscribe((updatedPostVM: PostViewModel) => {
            Object.assign(postVM, updatedPostVM);
            const rns = this.injector.get(ResponseNotificationService);
            if (updatedPostVM.featuredAt) {
              rns.emitMessage(this.translate.instant('common.post_marked_as_featured'));
            } else {
              rns.emitMessage(this.translate.instant('common.post_unmarked_as_featured'));
            }
            this.clearMenuData();
            this.ls.hide();
          }));
        }
      }] : []),
      {
        label: this.translate.instant('common.report_a_violation'),
        icon: 'pi pi-fw pi-exclamation-triangle text-orange-500',
        command: () => this.showReportViolationModal(postVM.apiModel, ViolationTypeEnum.POST)
      },
      ...(isPostOwner ? [{
        label: this.translate.instant('common.remove') + ' ' + this.translate.instant('common.post'),
        icon: 'pi pi-fw pi-trash',
        styleClass: 'p-button-danger',
        command: () => this.showRemovePostConfirmModal()
      }] : []),
    ];
  }

  private initCommentSlideMenu(commentAM: CommentApiModel): void {
    this.items = [
      {
        label: this.translate.instant('common.update') + ' ' + this.translate.instant('common.comment'),
        icon: 'pi pi-fw pi-pencil text-primary',
        command: (e) => {
          this.sidebarAction = 'editComment';
          this.cdr.detectChanges();
          this.startCommentEditSizing();
        }
      },
      {
        label: this.translate.instant('common.report_a_violation'),
        icon: 'pi pi-fw pi-exclamation-triangle text-orange-500',
        command: () => this.showReportViolationModal(commentAM, ViolationTypeEnum.COMMENT)
      },
      {
        label: this.translate.instant('common.remove') + ' ' + this.translate.instant('common.comment'),
        icon: 'pi pi-fw pi-trash',
        styleClass: 'p-button-danger',
        command: () => this.showRemoveCommentConfirmModal()
      },
    ];
  }

  private createForm(): void {
    this.form = this.fb.group(
      {
        content: [null, [
          RxwebValidators.minLength({ value: 1 }),
          RxwebValidators.maxLength({ value: 650000 })
        ]],
      }
    );
  }

  public prependToPostCollection(post: PostViewModel): void {
    // this.dataSource.adapter.prepend({items: [post], bof: false});
    this.posts = [...[post], ...this.posts];
    this.emitLoadedPosts();
    this.syncProcessingEventTopics();
    this.scheduleGridSync('prepend-post', 'prepend-post');
  }

  public registerOptimisticPost(post: PostViewModel, localThumbnailUri: string | null): void {
    if (!post?.id) {
      return;
    }

    this.inFlightOwnedPostIds.add(post.id);
    if (localThumbnailUri) {
      this.localGridThumbnailByPostId.set(post.id, localThumbnailUri);
      post.localThumbnailUri = localThumbnailUri;
    }
  }

  public insertOptimisticPostIntoCollection(post: PostViewModel, localThumbnailUri: string | null): void {
    if (!post) {
      return;
    }

    this.registerOptimisticPost(post, localThumbnailUri);
    post.postedBy = post.postedBy || this.currentUser;
    post.isPlaceholder = false;
    this.prependToPostCollection(post);
  }

  public resolveOptimisticPost(optimisticId: number | undefined, persistedPost: PostViewModel): void {
    if (!optimisticId || !persistedPost?.id) {
      return;
    }

    if (this.inFlightOwnedPostIds.has(optimisticId)) {
      this.inFlightOwnedPostIds.delete(optimisticId);
      this.inFlightOwnedPostIds.add(persistedPost.id);
    }

    if (this.localGridThumbnailByPostId.has(optimisticId)) {
      const localUri = this.localGridThumbnailByPostId.get(optimisticId);
      this.localGridThumbnailByPostId.delete(optimisticId);
      if (localUri) {
        this.localGridThumbnailByPostId.set(persistedPost.id, localUri);
        persistedPost.localThumbnailUri = localUri;
      }
    }
  }

  public replaceOptimisticPostInCollection(optimisticPost: PostViewModel, persistedPost: PostViewModel): void {
    if (!optimisticPost?.id || !persistedPost?.id) {
      return;
    }

    const postIndex = this.findPostIndexByReference(optimisticPost);
    if (postIndex < 0) {
      return;
    }

    persistedPost.postedBy = persistedPost.postedBy || this.currentUser;
    persistedPost.isPlaceholder = false;
    persistedPost.localThumbnailUri = optimisticPost.localThumbnailUri || persistedPost.localThumbnailUri || null;

    this.resolveOptimisticPost(optimisticPost.id, persistedPost);

    const nextPosts = [...this.posts];
    nextPosts[postIndex] = persistedPost;
    this.posts = nextPosts;
    this.emitLoadedPosts();
    this.syncProcessingEventTopics();
    this.scheduleGridSync('replace-optimistic-post', 'replace-optimistic-post');
  }

  public markOptimisticPostFailed(postId: number | undefined): void {
    if (!postId) {
      return;
    }

    this.inFlightOwnedPostIds.delete(postId);
    this.localGridThumbnailByPostId.delete(postId);
    this.backendGridThumbnailReadyByPostId.delete(postId);
  }

  public removeOptimisticPostFromCollection(post: PostViewModel): void {
    if (!post?.id) {
      return;
    }

    this.markOptimisticPostFailed(post.id);
    this.removePostFromPostCollection(post);
  }

  public removePostFromPostCollection(post: PostViewModel): void {
    this.posts = this.posts.filter(e => e.id !== post.id);
    this.emitLoadedPosts();
    this.syncProcessingEventTopics();
    this.scheduleGridSync('remove-post', 'remove-post');
  }

  public async openPostContextMenu(post: PostViewModel): Promise<void> {
    this.stopCommentEditSizing();
    this.commentEditMiddleHeight = null;
    this.initPostSlideMenu(post);
    this.sidebarAction = null;

    this.contextMenuObject = {
      author: post.postedBy,
      avatar: post.postedBy.avatar,
      content: post.content,
      mediaObjectAMs: [...(post.media || [])],
      createdAt: post.createdAt,
      model: post
    };

    await this.presentActionSheet();
  }

  public async openCommentContextMenu(commentAM: CommentApiModel): Promise<void> {
    this.stopCommentEditSizing();
    this.commentEditMiddleHeight = null;
    this.initCommentSlideMenu(commentAM);
    this.sidebarAction = null;

    this.contextMenuObject = {
      author: commentAM.author,
      avatar: commentAM.author.avatar as MediaObjectInterface,
      content: commentAM.content,
      mediaObjectAMs: [...(commentAM.media || [])],
      createdAt: commentAM.createdAt,
      model: commentAM
    };
    this.form.patchValue({ content: commentAM.content });
    await this.presentActionSheet();
  }

  private async presentActionSheet(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.actionSheetMounted) {
      this.actionSheetMounted = true;
      this.cdr.detectChanges();
      await this.waitForBottomSheetRef(() => this.timelineActionBottomSheet);
    } else {
      this.cdr.detectChanges();
    }

    const sheet = this.timelineActionBottomSheet;
    if (!sheet) {
      return;
    }

    await BottomSheetComponent.closeAllPresented(sheet);
    if (!sheet.isPresented()) {
      await sheet.toggleBottomSheet();
    } else {
      await sheet.moveToMiddle();
    }
  }

  public get actionSheetMiddleHeight(): number {
    if (this.sidebarAction === 'editComment' && this.commentEditMiddleHeight !== null) {
      return this.commentEditMiddleHeight;
    }
    return Math.max(160, (this.items?.length || 1) * 52 + 64);
  }

  public onActionSheetBack(): void {
    this.stopCommentEditSizing();
    this.sidebarAction = null;
    this.commentEditMiddleHeight = null;
    this.cdr.detectChanges();
    const sheet = this.timelineActionBottomSheet;
    if (sheet) {
      void sheet.updateMiddleBreakHeight(this.actionSheetMiddleHeight).then(() => {
        if (sheet.isPresented()) {
          return sheet.moveToMiddle();
        }
      });
    }
  }

  public onActionSheetDismissed(): void {
    this.stopCommentEditSizing();
    this.contextMenuObject = null;
    this.sidebarAction = null;
    this.commentEditMiddleHeight = null;
    this.actionSheetMounted = false;
    this.cdr.detectChanges();
  }

  private startCommentEditSizing(): void {
    this.stopCommentEditSizing();
    const editBody = this.commentEditBody?.nativeElement;
    if (editBody && typeof ResizeObserver !== 'undefined') {
      this.commentEditResizeObserver = new ResizeObserver(() => this.scheduleCommentEditResize());
      this.commentEditResizeObserver.observe(editBody);
    }
    this.scheduleCommentEditResize();
  }

  public scheduleCommentEditResize(): void {
    if (this.sidebarAction !== 'editComment' || this.commentEditResizeFrame !== null) {
      return;
    }

    const view = this.document.defaultView;
    if (!view) {
      return;
    }
    this.commentEditResizeFrame = view.requestAnimationFrame(() => {
      this.commentEditResizeFrame = null;
      this.measureCommentEditHeight();
    });
  }

  private measureCommentEditHeight(): void {
    const editBody = this.commentEditBody?.nativeElement;
    const sheetBody = editBody?.parentElement;
    if (this.sidebarAction !== 'editComment' || !editBody || !sheetBody) {
      return;
    }

    const view = this.document.defaultView;
    if (!view) {
      return;
    }
    const styles = view.getComputedStyle(sheetBody);
    const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const handle = this.timelineActionBottomSheet?.bottomSheetContainer?.nativeElement
      .querySelector('.bottom-sheet-drag-handle') as HTMLElement | null;
    const height = Math.ceil((handle?.offsetHeight ?? 40) + verticalPadding + editBody.getBoundingClientRect().height + 2);
    if (!Number.isFinite(height) || Math.abs(height - (this.commentEditMiddleHeight ?? 0)) < 2) {
      return;
    }

    const isInitialMeasurement = this.commentEditMiddleHeight === null;
    this.commentEditMiddleHeight = height;
    const sheet = this.timelineActionBottomSheet;
    if (sheet) {
      void sheet.updateMiddleBreakHeight(height).then(() => {
        if (isInitialMeasurement && this.sidebarAction === 'editComment' && sheet.isPresented()) {
          return sheet.moveToMiddle();
        }
      });
    }
  }

  private stopCommentEditSizing(): void {
    this.commentEditResizeObserver?.disconnect();
    this.commentEditResizeObserver = null;
    if (this.commentEditResizeFrame !== null) {
      this.document.defaultView?.cancelAnimationFrame(this.commentEditResizeFrame);
      this.commentEditResizeFrame = null;
    }
  }

  public async openMobileCommentsSheet(post: PostViewModel): Promise<void> {
    if (!this.isMobileViewport()) {
      return;
    }

    const isSamePost = this.mobileCommentsPost?.id === post.id;
    this.mobileCommentsPost = post;

    if (!this.mobileCommentsSheetMounted) {
      this.mobileCommentsSheetMounted = true;
      this.cdr.detectChanges();
      await this.waitForBottomSheetRef(() => this.timelineCommentsBottomSheet);
    } else {
      this.cdr.detectChanges();
    }

    if (!this.timelineCommentsBottomSheet) {
      return;
    }

    const isPresented = this.timelineCommentsBottomSheet.isPresented();
    if (isPresented && isSamePost) {
      await this.timelineCommentsBottomSheet.toggleBottomSheet();
      return;
    }

    if (!isPresented) {
      await BottomSheetComponent.closeAllPresented(this.timelineCommentsBottomSheet);
      await this.timelineCommentsBottomSheet.toggleBottomSheet();
      return;
    }

    await this.timelineCommentsBottomSheet.moveToMiddle();
  }

  public onMobileCommentsSheetDismissed(): void {
    if (!this.mobileCommentsSheetMounted) {
      return;
    }

    this.mobileCommentsSheetMounted = false;
    this.mobileCommentsPost = null;
    this.cdr.detectChanges();
  }

  public onMobileCommentsThreadListUnfolded(isUnfolded: boolean): void {
    if (!this.timelineCommentsBottomSheet) {
      return;
    }

    if (isUnfolded) {
      void this.timelineCommentsBottomSheet.moveToTop();
      return;
    }

    void this.timelineCommentsBottomSheet.moveToMiddle();
  }

  public updateComment(event: SocialInputSubmitInterface): void {
    const model = this.contextMenuObject.model;
    this.subscriptions.push(this.timelineDPS.updateComment({
      content: event.content,
      media: event.mediaObjectAMs.map(item => item['@id']),
      '@id': this.contextMenuObject.model['@id']
    }).subscribe({
      next: (commentAM: CommentApiModel) => {
        model.content = commentAM.content;
        model.media = commentAM.media;
        this.findTimelineTileByPostIri(commentAM.post)?.upsertComment(commentAM, true);
        this.mobileSocialActions?.upsertComment(commentAM);
        event.input.reset();
        this.clearMenuData();
      },
      error: () => event.input.restoreAfterFailedSubmit()
    }));
  }

  private async showReportViolationModal(subject: AbstractApiModel, type: ViolationTypeEnum): Promise<void> {
    await this.timelineActionBottomSheet?.closeIfPresented();
    this.subscriptions.push(this.modalService.showReportViolationModal({ subject: subject, type }).successState.subscribe(() => {
      this.clearMenuData();
    }));
  }

  private async showRemovePostConfirmModal(): Promise<void> {
    const post = this.contextMenuObject?.model as PostViewModel;
    if (!post) {
      return;
    }
    await this.timelineActionBottomSheet?.closeIfPresented();
    this.confirmationService.confirm({
      key: 'timeline',
      header: this.translate.instant('common.remove') + ' ' + this.translate.instant('common.post'),
      message: this.translate.instant('shared.timeline.post.remove_confirm'),
      accept: () => {
        this.ls.show();
        this.subscriptions.push(this.timelineDPS.removePost(post).subscribe(() => {
          this.removePostFromPostCollection(post);
          this.clearMenuData();
          this.ls.hide();
        }));
      },
      reject: () => {
        this.clearMenuData();
      }
    });
  }

  private async showRemoveCommentConfirmModal(): Promise<void> {
    const commentAM = this.contextMenuObject?.model as CommentApiModel;
    if (!commentAM) {
      return;
    }
    await this.timelineActionBottomSheet?.closeIfPresented();
    this.confirmationService.confirm({
      key: 'timeline',
      header: this.translate.instant('common.remove') + ' ' + this.translate.instant('common.comment'),
      message: this.translate.instant('shared.timeline.comment.remove_confirm'),
      accept: () => {
        this.ls.show();
        this.subscriptions.push(this.timelineDPS.removeComment(commentAM).subscribe(() => {
          this.applyRemovedCommentToPostCollection(commentAM);
          this.findTimelineTileByPostIri(commentAM.post)?.removeComment(commentAM);
          this.clearMenuData();
          this.ls.hide();
        }, () => {
          this.ls.hide();
        }));
      },
      reject: () => {
        this.clearMenuData();
      }
    });
  }

  private clearMenuData(): void {
    if (this.timelineActionBottomSheet?.isPresented()) {
      void this.timelineActionBottomSheet.closeIfPresented();
    } else {
      this.onActionSheetDismissed();
    }
  }

  private registerPostEventSource(): void {
    this.subscriptions.push(this.eventSource.listenSingleTopic<PostApiModel>(`/posts`).pipe(map((es: EventSourceInterface<PostApiModel>) => {
      const postAM = plainToClass(PostApiModel, es.payload as object);
      let postVM = this.posts.find(e => e['@id'] === postAM['@id']);

      if (postVM) {
        return this.mergePostEventIntoExistingPost(postVM, postAM, es.state);
      }

      postVM = new PostViewModel(postAM);
      postVM.state = es.state;
      return postVM;

    })).subscribe((postVM: PostViewModel) => {
      const belongsToCurrentFeed = this.isPostInCurrentFeed(postVM);
      const existsInCollection = this.posts.some((existingPost) => existingPost?.id === postVM?.id || existingPost?.['@id'] === postVM?.['@id']);

      if (!belongsToCurrentFeed && !existsInCollection) {
        return;
      }

      if (postVM.postedBy?.slug === this.currentUser?.slug) {
        return;
      }

      switch (postVM.state) {
        case 'persisted':
          if (!belongsToCurrentFeed) {
            return;
          }
          this.prependToPostCollection(postVM);
          break;
        case 'updated':
          this.replacePostInCollection(postVM.id, postVM);
          this.syncProcessingEventTopics();
          break;
        case 'removed':
          this.removePostFromPostCollection(postVM);
          break;
      }

      this.cdr.detectChanges();
    }));
  }

  private isPostInCurrentFeed(postVM: PostViewModel | null | undefined): boolean {
    if (!postVM) {
      return false;
    }

    if (this.projectVM && !this.areResourcesEqual(postVM.project, this.projectVM)) {
      return false;
    }

    if (this.eventAM && !this.areResourcesEqual(postVM.event, this.eventAM)) {
      return false;
    }

    if (this._postTypes?.length && postVM.type && !this._postTypes.includes(postVM.type)) {
      return false;
    }

    if (this.mediaType && !postVM.media?.some((media) => media?.type === this.mediaType)) {
      return false;
    }

    return true;
  }

  private areResourcesEqual(
    left: {id?: number | string; slug?: string; ['@id']?: string} | null | undefined,
    right: {id?: number | string; slug?: string; ['@id']?: string} | null | undefined
  ): boolean {
    if (!left || !right) {
      return false;
    }

    if (left['@id'] && right['@id'] && left['@id'] === right['@id']) {
      return true;
    }

    if (left.id !== null && left.id !== undefined && right.id !== null && right.id !== undefined) {
      return String(left.id) === String(right.id);
    }

    if (left.slug && right.slug) {
      return left.slug === right.slug;
    }

    return false;
  }

  private mergePostEventIntoExistingPost(
    currentPost: PostViewModel,
    postAM: PostApiModel,
    state: EventSourceInterface<PostApiModel>['state']
  ): PostViewModel {
    const nextPost = Object.assign(new PostViewModel(currentPost.apiModel), currentPost);
    const incomingPost = new PostViewModel(postAM);

    nextPost.state = state;
    nextPost.content = incomingPost.content;
    nextPost.media = [...(incomingPost.media || [])];
    nextPost.processingStatus = incomingPost.processingStatus;
    nextPost.processingUpdatedAt = incomingPost.processingUpdatedAt;
    nextPost.updatedAt = incomingPost.updatedAt;
    nextPost.featuredAt = incomingPost.featuredAt;
    nextPost.comments = incomingPost.comments || nextPost.comments;
    nextPost.commentsCount = incomingPost.commentsCount ?? nextPost.commentsCount;
    nextPost.reactions = incomingPost.reactions || nextPost.reactions;
    nextPost.reactionsPreview = incomingPost.reactionsPreview || nextPost.reactionsPreview;
    nextPost.reactionsCount = incomingPost.reactionsCount ?? nextPost.reactionsCount;
    nextPost.currentUserReaction = incomingPost.currentUserReaction ?? nextPost.currentUserReaction ?? null;
    nextPost.sharesCount = incomingPost.sharesCount ?? nextPost.sharesCount;
    nextPost.type = incomingPost.type || nextPost.type;
    nextPost.shareType = incomingPost.shareType ?? nextPost.shareType ?? null;
    nextPost.shareId = incomingPost.shareId ?? nextPost.shareId ?? null;
    nextPost.shareRef = incomingPost.shareRef ?? nextPost.shareRef ?? null;
    nextPost.shareCard = incomingPost.shareCard ?? nextPost.shareCard ?? null;

    if (incomingPost.postedBy?.slug) {
      nextPost.postedBy = incomingPost.postedBy;
    }
    if (incomingPost.project) {
      nextPost.project = incomingPost.project;
    }
    if (incomingPost.event) {
      nextPost.event = incomingPost.event;
    }
    if (incomingPost.collaboration) {
      nextPost.collaboration = incomingPost.collaboration;
    }

    return nextPost;
  }

  private registerCommentEventSource(): void {
    this.subscriptions.push(this.eventSource.listenSingleTopic<CommentApiModel>(`/comments`).subscribe((es: EventSourceInterface<CommentApiModel>) => {
      const commentAM = es.payload;
      const timelineTileComponent = this.findTimelineTileByPostIri(commentAM.post);

      switch (es.state) {
        case 'persisted':
        case 'updated':
          if (commentAM.author?.slug === this.currentUser?.slug) {
            break;
          }
          this.applyUpsertedCommentToPostCollection(commentAM);
          timelineTileComponent?.upsertComment(commentAM);
          this.mobileSocialActions?.upsertComment(commentAM);
          break;
        case 'removed':
          this.applyRemovedCommentToPostCollection(commentAM);
          timelineTileComponent?.removeComment(commentAM);
      }

      this.cdr.detectChanges();
    }));
  }

  private findTimelineTileByPostIri(postIri: string | null | undefined): TimelineTileComponent | undefined {
    if (!postIri) {
      return undefined;
    }

    return this.timelineTileComponents?.find((tile) => tile.post['@id'] === postIri);
  }

  private applyUpsertedCommentToPostCollection(commentAM: CommentApiModel): void {
    this.patchCommentCountForPost(commentAM, 'upsert');
  }

  private applyRemovedCommentToPostCollection(commentAM: CommentApiModel): void {
    this.patchCommentCountForPost(commentAM, 'remove');
  }

  private patchCommentCountForPost(commentAM: CommentApiModel, mode: 'upsert' | 'remove'): void {
    if (!commentAM?.post || commentAM.thread) {
      return;
    }

    const postIndex = this.posts.findIndex((post) => post['@id'] === commentAM.post);
    if (postIndex < 0) {
      return;
    }

    const currentPost = this.posts[postIndex];
    const currentComments = Array.isArray(currentPost.comments) ? [...currentPost.comments] : [];

    if (mode === 'upsert' && currentComments.includes(commentAM['@id'])) {
      return;
    }

    if (mode === 'remove' && !currentComments.includes(commentAM['@id'])) {
      return;
    }

    const nextPost = Object.assign(new PostViewModel(currentPost.apiModel), currentPost);
    nextPost.comments = mode === 'upsert'
      ? [commentAM['@id'], ...currentComments]
      : currentComments.filter((commentIri) => commentIri !== commentAM['@id']);
    nextPost.commentsCount = mode === 'upsert'
      ? (nextPost.commentsCount || 0) + 1
      : Math.max(0, (nextPost.commentsCount || 0) - 1);

    if (nextPost.apiModel) {
      (nextPost.apiModel as any).comments = nextPost.comments;
      (nextPost.apiModel as any).commentsCount = nextPost.commentsCount;
    }

    this.replacePostInCollection(nextPost.id, nextPost);
  }

  private syncProcessingEventTopics(): void {
    const nextTopics = this.resolveProcessingTopics(this.posts);
    if (this.areSameTopics(this.activeProcessingTopics, nextTopics)) {
      return;
    }

    if (this.activeProcessingTopics.length) {
      this.eventSource.closeArrayOfTopics(this.activeProcessingTopics.map((topic) => `topic=${topic}`));
    }

    this.activeProcessingTopics = nextTopics;
    if (!this.activeProcessingTopics.length) {
      return;
    }

    this.subscriptions.push(this.eventSource
      .listenArrayOfTopics<PostProcessingEventInterface>(this.activeProcessingTopics.map((topic) => `topic=${topic}`), false)
      .subscribe((es: EventSourceInterface<PostProcessingEventInterface>) => {
        const eventPayload = es?.payload;
        if (!eventPayload?.postId) {
          return;
        }
        if (this.isDuplicateProcessingEvent(eventPayload)) {
          return;
        }

        const post = this.posts.find((postVM) => postVM.id === eventPayload.postId);
        if (!post) {
          return;
        }

        const nextPost = this.applyProcessingUpdateToPost(post, eventPayload);
        this.replacePostInCollection(post.id, nextPost);

        if (nextPost.id && this.isTerminalPostStatus(nextPost.processingStatus)) {
          this.inFlightOwnedPostIds.delete(nextPost.id);
        }

        this.syncProcessingEventTopics();
        this.cdr.detectChanges();
      }));
  }

  private resolveProcessingTopics(posts: PostViewModel[]): string[] {
    const topics = new Set<string>();

    (posts || []).forEach((post) => {
      if (!this.isPersistedServerPostId(post?.id) || post.isPlaceholder) {
        return;
      }

      if (this.postHasProcessingMedia(post)) {
        topics.add(`/posts/${post.id}/processing`);
      }
    });

    return Array.from(topics).sort();
  }

  private areSameTopics(currentTopics: string[], nextTopics: string[]): boolean {
    if (currentTopics.length !== nextTopics.length) {
      return false;
    }

    return currentTopics.every((topic, index) => topic === nextTopics[index]);
  }

  private postHasProcessingMedia(post: PostViewModel | null | undefined): boolean {
    if (!this.isPersistedServerPostId(post?.id)) {
      return false;
    }

    if ((post.processingStatus === 'pending' || post.processingStatus === 'processing') && !(post.media || []).length) {
      return true;
    }

    return !!post.media?.some((media: any) => {
      const status = (media?.processingStatus || '').toString();
      return status === 'pending_upload' || status === 'uploaded' || status === 'processing';
    });
  }

  private derivePostStatusFromMedia(mediaCollection: Array<any>): string | null {
    if (!mediaCollection.length) {
      return null;
    }

    const statuses = mediaCollection
      .map((media) => (media?.processingStatus || '').toString())
      .filter(Boolean);

    if (!statuses.length) {
      return null;
    }

    if (statuses.every((status) => status === 'failed')) {
      return 'failed';
    }

    if (statuses.every((status) => status === 'completed')) {
      return 'completed';
    }

    const completedCount = statuses.filter((status) => status === 'completed').length;
    const failedCount = statuses.filter((status) => status === 'failed').length;
    if (completedCount > 0 && failedCount > 0) {
      return 'partially_completed';
    }

    if (statuses.some((status) => status === 'uploaded' || status === 'processing')) {
      return 'processing';
    }

    return 'pending';
  }

  private applyProcessingUpdateToPost(post: PostViewModel, eventPayload: PostProcessingEventInterface): PostViewModel {
    const nextPost = Object.assign(new PostViewModel(post.apiModel), post);
    const isPostLevelEvent = eventPayload.eventType === 'post.processing.updated' || eventPayload.eventType === 'post.ready';
    const shouldApplyPostStatus = isPostLevelEvent && this.shouldApplyProcessingEvent(eventPayload);

    if (shouldApplyPostStatus) {
      nextPost.processingStatus = eventPayload.status as any;
      nextPost.processingUpdatedAt = eventPayload.updatedAt ? new Date(eventPayload.updatedAt) : nextPost.processingUpdatedAt;
    }

    let nextMedia = [...(nextPost.media || [])];

    if (eventPayload.mediaId) {
      nextMedia = this.applySingleMediaProcessingUpdate(nextMedia, eventPayload);
    }

    if (eventPayload.post?.media?.length) {
      nextMedia = this.applyPostMediaSnapshot(nextMedia, eventPayload.post.media as Array<any>);
    }

    nextPost.media = nextMedia;

    if (!isPostLevelEvent) {
      const derivedStatus = this.derivePostStatusFromMedia(nextMedia);
      if (derivedStatus) {
        nextPost.processingStatus = derivedStatus as any;
      }
    }

    if (nextPost.id && this.hasBackendGridThumbnail(nextPost)) {
      this.promoteBackendThumbnailAfterWarmup(nextPost);
    }

    if (
      eventPayload.mediaId
      && eventPayload.status === 'completed'
      && nextPost.id
      && !this.hasBackendGridThumbnail(nextPost)
    ) {
      this.refreshBackendMediaStateForThumbnail(nextPost, eventPayload.mediaId);
    }

    return nextPost;
  }

  private applySingleMediaProcessingUpdate(
    mediaCollection: Array<any>,
    eventPayload: PostProcessingEventInterface
  ): Array<any> {
    const mediaIndex = mediaCollection.findIndex((item) => item?.id === eventPayload.mediaId);
    if (mediaIndex < 0) {
      return [...mediaCollection];
    }

    const currentMedia = mediaCollection[mediaIndex] as any;
    const nextMedia = {
      ...currentMedia,
      processingStatus: eventPayload.status as any,
      processingError: eventPayload.error?.message ?? currentMedia?.processingError,
      hyperlinks: this.mergeMediaHyperlinks(currentMedia?.hyperlinks, eventPayload.urls?.hyperlinks),
    } as any;

    const updatedCollection = [...mediaCollection];
    updatedCollection[mediaIndex] = nextMedia;

    return updatedCollection;
  }

  private applyPostMediaSnapshot(mediaCollection: Array<any>, snapshotMedia: Array<any>): Array<any> {
    const updatedCollection = [...mediaCollection];
    const snapshotById = new Map(snapshotMedia.map((item) => [item?.id, item]));

    return updatedCollection.map((currentMedia: any) => {
      const incoming = snapshotById.get(currentMedia?.id);
      if (!incoming) {
        return currentMedia;
      }

      return {
        ...currentMedia,
        ...(incoming.processingStatus ? {processingStatus: incoming.processingStatus} : {}),
        ...(incoming.processingError !== undefined ? {processingError: incoming.processingError} : {}),
        hyperlinks: this.mergeMediaHyperlinks(currentMedia?.hyperlinks, incoming.hyperlinks),
      };
    });
  }

  private mergeMediaHyperlinks(currentHyperlinks: any, incomingHyperlinks: any): any[] {
    const current = Array.isArray(currentHyperlinks) ? currentHyperlinks : [];
    const incoming = Array.isArray(incomingHyperlinks) ? incomingHyperlinks.filter((item) => !!item?.name && !!item?.uri) : [];

    if (!incoming.length) {
      return [...current];
    }

    const merged = new Map<string, any>();
    current.forEach((item: any) => {
      if (item?.name && item?.uri) {
        merged.set(item.name, item);
      }
    });
    incoming.forEach((item: any) => {
      merged.set(item.name, item);
    });

    return Array.from(merged.values());
  }

  private replacePostInCollection(postId: number, nextPost: PostViewModel): void {
    const postIndex = this.posts.findIndex((postVM) => postVM.id === postId);
    if (postIndex < 0) {
      return;
    }

    const nextPosts = [...this.posts];
    nextPosts[postIndex] = nextPost;
    this.posts = nextPosts;
    this.emitLoadedPosts();
    this.syncProcessingEventTopics();
    this.scheduleGridSync('replace-post', 'replace-post');
  }

  private findPostIndexByReference(post: PostViewModel): number {
    if (!post) {
      return -1;
    }

    return this.posts.findIndex((candidate) => {
      if (!candidate) {
        return false;
      }

      if (post.id && candidate.id === post.id) {
        return true;
      }

      return !!post['@id'] && candidate['@id'] === post['@id'];
    });
  }

  private shouldApplyProcessingEvent(eventPayload: PostProcessingEventInterface): boolean {
    const currentVersion = this.processingVersionsByPostId.get(eventPayload.postId) || 0;
    const nextVersion = eventPayload.version || 0;
    const nextStatus = eventPayload.status || '';
    const currentStatus = this.processingStatusByPostId.get(eventPayload.postId) || '';

    if (nextVersion < currentVersion) {
      return false;
    }

    // Allow equal-version events when status actually changes (same-ms backend events).
    if (nextVersion === currentVersion && nextStatus === currentStatus) {
      return false;
    }

    this.processingVersionsByPostId.set(eventPayload.postId, nextVersion || Date.now());
    this.processingStatusByPostId.set(eventPayload.postId, nextStatus);
    return true;
  }

  private isDuplicateProcessingEvent(eventPayload: PostProcessingEventInterface): boolean {
    const key = `${eventPayload.postId}:${eventPayload.mediaId || 0}:${eventPayload.status || ''}:${eventPayload.version || 0}`;
    const now = Date.now();
    const ttlMs = 7000;
    const expiresAt = this.recentProcessingEventKeys.get(key) || 0;
    if (expiresAt > now) {
      return true;
    }

    this.recentProcessingEventKeys.set(key, now + ttlMs);
    if (this.recentProcessingEventKeys.size > 300) {
      for (const [eventKey, eventExpiresAt] of this.recentProcessingEventKeys.entries()) {
        if (eventExpiresAt <= now) {
          this.recentProcessingEventKeys.delete(eventKey);
        }
      }
    }

    return false;
  }

  private refreshBackendMediaStateForThumbnail(post: PostViewModel, mediaId: number): void {
    if (!mediaId || this.backendThumbnailRefreshInFlightByMediaId.has(mediaId)) {
      return;
    }
    this.backendThumbnailRefreshInFlightByMediaId.add(mediaId);

    const mediaIri = `/api/media_objects/${mediaId}`;
    this.subscriptions.push(this.mediaObjectRS.getMediaObject(mediaIri).subscribe({
      next: (mediaAM) => {
        const media = (post.media || []).find((item) => item?.id === mediaId);
        if (media) {
          media.hyperlinks = (mediaAM as any)?.hyperlinks || media.hyperlinks;
          media.processingStatus = (mediaAM as any)?.processingStatus || media.processingStatus;
          media.processingError = (mediaAM as any)?.processingError || media.processingError;
        }
        if (post.id && this.hasBackendGridThumbnail(post)) {
          this.promoteBackendThumbnailAfterWarmup(post);
        }
      },
      complete: () => this.backendThumbnailRefreshInFlightByMediaId.delete(mediaId),
      error: () => this.backendThumbnailRefreshInFlightByMediaId.delete(mediaId),
    }));
  }

  addPost(event: SocialInputSubmitInterface) {
    if (event.content.length < 1) {
      return;
    }
    this.subscriptions.push(this.timelineDPS.createPost(PostViewModel.create(
      event.content,
      this.postAsPostType,
      event.mediaObjectAMs,
      this.projectVM,
      this.eventAM
    ))
      .subscribe((postVM: PostViewModel) => {
        postVM.postedBy = this.currentUser;
        if (postVM.id && (this.isInProgressStatus(postVM.processingStatus) || this.hasInProgressPrimaryMedia(postVM) || !postVM.media?.length)) {
          this.inFlightOwnedPostIds.add(postVM.id);
        }
        this.prependToPostCollection(postVM);
      }));
  }

  public openPostComposer(sharedPost: PostViewModel | null = null): void {
    if (!this.currentUser) {
      return;
    }

    this.subscriptions.push(this.modalService.showPostComposerModal({
      currentUserVM: this.currentUser,
      projectVM: this.projectVM || null,
      eventAM: this.eventAM || null,
      sharedPost,
    }).successState.subscribe((modalStateEvent) => {
      const postVM = modalStateEvent?.data as PostViewModel;
      if (!postVM) {
        return;
      }

      postVM.postedBy = this.currentUser;
      this.prependToPostCollection(postVM);
    }));
  }

  public onSharedPostCreated(postVM: PostViewModel): void {
    if (!postVM) {
      return;
    }

    postVM.postedBy = this.currentUser;
    this.prependToPostCollection(postVM);
  }

  public onShareCountIncremented(event: { postId: number; delta: number }): void {
    if (!event?.postId || !event?.delta) {
      return;
    }

    const currentPost = this.posts.find((postVM) => postVM?.id === event.postId);
    if (!currentPost) {
      return;
    }

    const nextPost = Object.assign(new PostViewModel(currentPost.apiModel), currentPost);
    nextPost.sharesCount = Math.max(0, (nextPost.sharesCount || 0) + event.delta);
    if (nextPost.apiModel) {
      nextPost.apiModel.sharesCount = nextPost.sharesCount;
    }

    this.replacePostInCollection(event.postId, nextPost);
  }

  public getComposerLabelKey(): string {
    if (this.projectVM) {
      return 'shared.timeline.post_label';
    }

    return 'shared.share.composer.global_placeholder';
  }

  private async openEditPostComposer(postToEdit: PostViewModel): Promise<void> {
    await this.timelineActionBottomSheet?.closeIfPresented();
    if (!this.currentUser) {
      return;
    }

    this.subscriptions.push(this.modalService.showPostComposerModal({
      currentUserVM: this.currentUser,
      editingPost: postToEdit,
    }).successState.subscribe((modalStateEvent) => {
      const updatedPost = modalStateEvent?.data as PostViewModel;
      if (updatedPost) {
        this.applyUpdatedPost(updatedPost);
      }
    }));
  }

  private applyUpdatedPost(post: PostViewModel): void {
    const currentPost = this.posts.find((item) => item['@id'] === post['@id']);
    if (post.id && (this.isInProgressStatus(post.processingStatus) || this.hasInProgressPrimaryMedia(post))) {
      this.inFlightOwnedPostIds.add(post.id);
      this.backendGridThumbnailReadyByPostId.delete(post.id);
      if (currentPost?.localThumbnailUri && !post.localThumbnailUri) {
        this.localGridThumbnailByPostId.set(post.id, currentPost.localThumbnailUri);
        post.localThumbnailUri = currentPost.localThumbnailUri;
      }
    }
    this.replacePostInCollection(post.id, post);
    this.cdr.detectChanges();
  }

  trackByVirtualItem(_: number, item: TimelineVirtualItem): string {
    return item.key;
  }

  trackByVirtualGridRow(_: number, row: TimelineVirtualGridRow): string {
    return row.key;
  }

  private appendLoadingPlaceholders(): void {
    if (this.hasIncompleteGridTerminalRow()) {
      this.endOfResults = true;
      return;
    }
    const appendPlaceholders = Array.from(
      { length: this.getAppendPlaceholderCount() },
      () => this.createAppendPlaceholder()
    );
    this.posts = [...this.removeAppendLoadingPlaceholders(), ...appendPlaceholders];
  }

  private createAppendPlaceholder(): PostViewModel {
    const placeholder = new PostViewModel();
    (placeholder as any)[this.appendPlaceholderMarker] = true;
    return placeholder;
  }

  private removeAppendLoadingPlaceholders(): PostViewModel[] {
    return this.posts.filter((post) => !this.isAppendPlaceholder(post));
  }

  private isAppendPlaceholder(post: PostViewModel | undefined): boolean {
    return !!post && !!(post as any)[this.appendPlaceholderMarker];
  }

  private getAppendPlaceholderCount(): number {
    return this.viewType === ViewTypeEnum.GRID ? 3 : 1;
  }

  private getLoadedPostsCount(posts: PostViewModel[]): number {
    return (posts || []).filter((post) => post && !post.isPlaceholder && !this.isAppendPlaceholder(post)).length;
  }

  /**
   * Media pages are emitted in complete grid rows. A partial final row is the
   * terminal page, so appending skeletons below it would falsely imply that
   * another page is available.
   */
  private hasIncompleteGridTerminalRow(posts: PostViewModel[] = this.posts): boolean {
    if (this.viewType !== ViewTypeEnum.GRID) {
      return false;
    }

    const loadedPostsCount = this.getLoadedPostsCount(posts);
    return loadedPostsCount > 0 && loadedPostsCount % this.resolveEffectiveGridColumns() !== 0;
  }

  private isMobileViewport(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    return (window.innerWidth || 0) < 768;
  }

  private async waitForBottomSheetRef(getSheet: () => BottomSheetComponent | undefined, maxFrames: number = 8): Promise<void> {
    for (let i = 0; i < maxFrames; i++) {
      if (getSheet()) {
        return;
      }
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      this.cdr.detectChanges();
    }
  }

  private maybeRequestNextPostsPage(): void {
    if (this.isSwipePreview) {
      return;
    }
    if (this.singlePostMode) {
      return;
    }

    if (this.isPostsPageLoading || this.hasPlaceholderPosts()) {
      return;
    }

    // Do not use the collection total as a hard stop here. Some API Platform
    // collection variants report a page-local total while more pages are still
    // available. The reliable terminal signal for an infinite timeline is an
    // empty next page (or an explicitly exhausted source).
    if (this.endOfResults) {
      this.endOfResults = true;
      return;
    }

    void this.loadNextPage();
  }

  /** Loads at most one next page at a time for an optional external consumer. */
  public loadNextPage(): Promise<boolean> {
    if (this.isSwipePreview) {
      return Promise.resolve(false);
    }
    if (this.nextPageLoadPromise) {
      return this.nextPageLoadPromise;
    }

    if (!this.canLoadNextPage || this.isPostsPageLoading) {
      return Promise.resolve(false);
    }

    this.nextPageLoadPromise = new Promise<boolean>((resolve) => {
      this.requestNextPostsPage(resolve);
    }).finally(() => {
      this.nextPageLoadPromise = null;
    });
    return this.nextPageLoadPromise;
  }

  public get canLoadNextPage(): boolean {
    return !this.isSwipePreview
      && !this.singlePostMode
      && !this.isPostsPageLoading
      && !this.endOfResults
      && !this.hasIncompleteGridTerminalRow()
      && !this.hasPlaceholderPosts();
  }

  private requestNextPostsPage(onComplete?: (loaded: boolean) => void): void {
    const interactionLabel = `append-page-${this.page}`;
    const startedAt = this.now();
    const requestContextVersion = this.feedContextVersion;

    this.isPostsPageLoading = true;
    queueMicrotask(() => {
      if (!this.isPostsPageLoading || requestContextVersion !== this.feedContextVersion) {
        return;
      }
      this.appendLoadingPlaceholders();
      this.scheduleGridSync(`append-placeholders:${interactionLabel}`, interactionLabel);
    });
    this.subscriptions.push(this.getPostCollection(this.page)
      .pipe(
        tap((result: TimelinePageResult) => {
          if (requestContextVersion !== this.feedContextVersion) {
            return;
          }
          this.totalItems = result.total;
        }),
        finalize(() => {
          if (requestContextVersion === this.feedContextVersion) {
            this.isPostsPageLoading = false;
          }
        })
      )
      .subscribe({
        next: (result: TimelinePageResult) => {
        if (requestContextVersion !== this.feedContextVersion) {
          return;
        }
        const postVMs = result.records;
        // this.ls.hide();
        if (!postVMs.length) {
          this.endOfResults = true;
        }

        this.posts = [...this.removeAppendLoadingPlaceholders(), ...postVMs];
        if (this.hasIncompleteGridTerminalRow()) {
          this.endOfResults = true;
        }
        this.emitLoadedPosts();
        this.syncProcessingEventTopics();
        this.page++;
        this.scheduleGridSync(`append-complete:${interactionLabel}`, interactionLabel, {
          startedAt,
          onComplete: () => {
            this.schedulePrewarmMedia(postVMs, interactionLabel);
            onComplete?.(postVMs.length > 0);
          }
        });
        },
        error: () => {
          this.posts = this.removeAppendLoadingPlaceholders();
          this.emitLoadedPosts();
          this.syncProcessingEventTopics();
          this.scheduleGridSync(`append-error:${interactionLabel}`, interactionLabel, {
            startedAt,
            onComplete: () => onComplete?.(false)
          });
        }
      }));
  }

  onPullToRefresh(pullToRefresh: PullToRefreshDirective): void {
    if (this.isSwipePreview || !this.pullToRefreshEnabled) {
      pullToRefresh.complete();
      return;
    }
    if (this.singlePostMode) {
      pullToRefresh.complete();
      return;
    }
    this.isRefreshing = true;
    this.cacheRefresh.requestRefresh();
    this.resetFeedStateForCurrentContext('refresh');
    this.loadPostsPage(1, () => {
      pullToRefresh.complete();
      this.isRefreshing = false;
    });
  }

  public forceRefreshFromParent(): void {
    if (this.isSwipePreview || this.singlePostMode) {
      return;
    }
    this.cacheRefresh.requestRefresh();
    this.resetFeedStateForCurrentContext('parent-refresh');
    this.loadPostsPage(1);
  }

  private loadPostsPage(page: number, onComplete?: () => void): void {
    if (this.isSwipePreview) {
      onComplete?.();
      return;
    }
    const interactionLabel = `load-page-${page}`;
    const startedAt = this.now();
    const requestContextVersion = this.feedContextVersion;
    this.isPostsPageLoading = true;
    this.subscriptions.push(this.getPostCollection(page)
      .pipe(
        tap((result: TimelinePageResult) => {
          if (requestContextVersion !== this.feedContextVersion) {
            return;
          }
          this.totalItems = result.total;
        }),
        finalize(() => {
          if (requestContextVersion === this.feedContextVersion) {
            this.isPostsPageLoading = false;
          }
        })
      )
      .subscribe({
        next: (result: TimelinePageResult) => {
          if (requestContextVersion !== this.feedContextVersion) {
            return;
          }
          const postVMs = result.records;
          if (!postVMs.length) {
            this.endOfResults = true;
          }

          this.posts = [...postVMs];
          if (this.hasIncompleteGridTerminalRow()) {
            this.endOfResults = true;
          }
          this.emitLoadedPosts();
          this.syncProcessingEventTopics();
          this.scheduleGridSync(`load-complete:${interactionLabel}`, interactionLabel, {
            startedAt,
            onComplete: () => {
              this.schedulePrewarmMedia(postVMs, interactionLabel);
              onComplete?.();
            }
          });
        },
        error: () => {
          if (requestContextVersion !== this.feedContextVersion) {
            return;
          }
          onComplete?.();
        }
      }));
  }

  private getPostCollection(page: number) {
    if (this.mediaType) {
      return this.timelineDPS.getPostCollection(this.projectVM, this.eventAM, page, this._postTypes, null, this.mediaType);
    }
    return this.timelineDPS.getPostCollection(this.projectVM, this.eventAM, page, this._postTypes);
  }

  private applyPreloadedFirstPage(result: TimelinePageResult): void {
    const postVMs = result.records || [];
    this.totalItems = result.total || 0;
    this.page = Math.max(2, (result.page || 1) + 1);
    this.endOfResults = !postVMs.length;
    this.posts = [...postVMs];
    if (this.hasIncompleteGridTerminalRow()) {
      this.endOfResults = true;
    }
    this.emitLoadedPosts();
    this.syncProcessingEventTopics();
    this.scheduleGridSync('preloaded-page', 'preloaded-page', {
      onComplete: () => this.schedulePrewarmMedia(postVMs, 'preloaded-page')
    });
  }

  /** Returns the state needed by a host to remount this timeline without reloading it. */
  public captureState(): TimelineStateSnapshot {
    return {
      // The array is replaced only when records change. Keeping this reference
      // avoids allocating a complete copy on every scroll/state update.
      records: this.loadedPosts,
      page: this.page,
      total: this.totalItems,
      endOfResults: this.endOfResults,
      scrollY: this.getScrollY(),
      ready: !this.hasPlaceholderPosts(),
    };
  }

  /** Small runtime diagnostic intended for development profiling. */
  public getRuntimeDiagnostics(): {activeTimelineInstances: number; loadedPosts: number; visibleItems: number} {
    return {
      activeTimelineInstances: TimelineComponent.activeInstances,
      loadedPosts: this.loadedPosts.length,
      visibleItems: this.viewType === ViewTypeEnum.GRID
        ? this.virtualVisibleGridRows.reduce((count, row) => count + row.items.length, 0)
        : this.virtualVisibleItems.length,
    };
  }

  private applyRestoredState(state: TimelineStateSnapshot): void {
    const records = state.records || [];
    this.posts = [...records];
    this.page = Math.max(2, state.page || 2);
    this.totalItems = state.total || 0;
    this.endOfResults = !!state.endOfResults;
    this.isPostsPageLoading = false;
    this.emitLoadedPosts();
    this.syncProcessingEventTopics();
    this.scheduleGridSync('restore-state', 'restore-state', {
      onComplete: () => this.restoreScrollPosition(state.scrollY),
    });
  }

  private getScrollY(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 0;
    }
    return window.scrollY || this.document.documentElement?.scrollTop || 0;
  }

  private restoreScrollPosition(scrollY: number): void {
    if (!isPlatformBrowser(this.platformId) || !Number.isFinite(scrollY) || scrollY <= 0) {
      return;
    }
    this.runOnNextFrame(() => this.runOnNextFrame(() => window.scrollTo({top: scrollY, behavior: 'auto'})));
  }

  navigateToList(item: any) {
    const targetPost = (item?.data as PostViewModel | undefined) || (item as PostViewModel | undefined);
    if (!targetPost?.id) {
      return;
    }
    const targetId = `item${targetPost.id}`;
    this.pendingPreviewTargetId = targetId;
    this.pendingPreviewIndex = this.posts.findIndex((post) => post?.id === targetPost.id);
    if (targetPost?.id && !targetPost.localThumbnailUri) {
      const fallbackThumb = this.getGridThumbnail(targetPost);
      if (fallbackThumb) {
        targetPost.localThumbnailUri = fallbackThumb;
      }
    }

    this.isViewSwitching = true;
    if (this.syncViewWithUrl) {
      this.persistGridStateBeforeListNavigation();
    }
    this.viewType = ViewTypeEnum.LIST;
    this.viewTypeChange.emit(this.viewType);
    if (this.syncViewWithUrl) {
      this.actionsService.viewType = this.viewType;
    }
    this.scheduleVirtualLayoutSync();

    if (this.syncViewWithUrl) {
      this.updateViewParamInUrl(ViewTypeEnum.LIST, true);
      setTimeout(() => this.preview(targetId));
    }
    setTimeout(() => {
      this.isViewSwitching = false;
    }, 350);
  }

  private updateViewParamInUrl(view: ViewTypeEnum, pushHistory = false): void {
    const currentUrl = this.location.path(true);
    const hashIndex = currentUrl.indexOf('#');
    const base = hashIndex >= 0 ? currentUrl.slice(0, hashIndex) : currentUrl;
    const hash = hashIndex >= 0 ? currentUrl.slice(hashIndex) : '';
    const queryIndex = base.indexOf('?');
    const path = queryIndex >= 0 ? base.slice(0, queryIndex) : base;
    const query = queryIndex >= 0 ? base.slice(queryIndex + 1) : '';
    const params = new URLSearchParams(query);
    params.set('view', view);
    const nextQuery = params.toString();
    const nextUrl = `${path}${nextQuery ? `?${nextQuery}` : ''}${hash}`;
    if (pushHistory) {
      this.location.go(nextUrl);
      return;
    }
    this.location.replaceState(nextUrl);
  }

  private applyViewFromQuery(viewFromQuery?: ViewTypeEnum): void {
    if (!this.syncViewWithUrl) {
      return;
    }
    if (viewFromQuery !== ViewTypeEnum.GRID && viewFromQuery !== ViewTypeEnum.LIST) {
      return;
    }
    if (this.viewType !== viewFromQuery) {
      this.viewType = viewFromQuery;
      this.viewTypeChange.emit(this.viewType);
      this.actionsService.viewType = this.viewType;
    }
    if (viewFromQuery === ViewTypeEnum.GRID) {
      this.restoreGridScrollFromHistoryState();
    }
    this.scheduleGridSync(`query-view:${viewFromQuery}`, `query-view:${viewFromQuery}`);
  }

  private persistGridStateBeforeListNavigation(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const currentUrl = this.location.path(true);
    const normalizedGridUrl = this.buildUrlWithView(currentUrl, ViewTypeEnum.GRID);
    const currentState = window.history.state || {};
    const nextState = {
      ...currentState,
      [this.gridScrollStateKey]: window.scrollY || window.pageYOffset || 0
    };
    window.history.replaceState(nextState, '', normalizedGridUrl);
  }

  private restoreGridScrollFromHistoryState(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const state = window.history.state || {};
    const raw = state?.[this.gridScrollStateKey];
    const targetY = Number(raw);
    if (!Number.isFinite(targetY) || targetY < 0) {
      return;
    }
    requestAnimationFrame(() => window.scrollTo({top: targetY, behavior: 'auto'}));
  }

  private buildUrlWithView(url: string, view: ViewTypeEnum): string {
    const hashIndex = url.indexOf('#');
    const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const hash = hashIndex >= 0 ? url.slice(hashIndex) : '';
    const queryIndex = base.indexOf('?');
    const path = queryIndex >= 0 ? base.slice(0, queryIndex) : base;
    const query = queryIndex >= 0 ? base.slice(queryIndex + 1) : '';
    const params = new URLSearchParams(query);
    params.set('view', view);
    const nextQuery = params.toString();
    return `${path}${nextQuery ? `?${nextQuery}` : ''}${hash}`;
  }

  private getViewQueryFromCurrentUrl(): ViewTypeEnum | undefined {
    const currentUrl = this.location.path(true);
    const hashIndex = currentUrl.indexOf('#');
    const pathAndQuery = hashIndex >= 0 ? currentUrl.slice(0, hashIndex) : currentUrl;
    const queryIndex = pathAndQuery.indexOf('?');
    if (queryIndex < 0) {
      return undefined;
    }
    const queryString = pathAndQuery.slice(queryIndex + 1);
    const params = new URLSearchParams(queryString);
    return params.get('view') as ViewTypeEnum | undefined;
  }

  private prewarmMedia(_posts: PostViewModel[], _reason: string): void {
    if (!this.prefetchEnabled || !this.mediaPrewarmEnabled) {
      this.clearMediaPrewarm();
      return;
    }

    const loadedPosts = (this.posts || []).filter((post) => !!post?.id && !post.isPlaceholder && !this.isAppendPlaceholder(post));
    if (!loadedPosts.length) {
      return;
    }
    const windowStart = this.resolvePrewarmWindowStart(loadedPosts.length);
    const firstMedia = loadedPosts
      .slice(windowStart, windowStart + this.prewarmWindowPostCount)
      .flatMap((post) => post?.media || [])
      .slice(0, this.prewarmWindowPostCount);
    if (firstMedia.length) {
      const imageUrls = firstMedia.map((media) => {
        if (!media) {
          return null;
        }
        if (media.type === MediaTypeEnum.VIDEO) {
          return MediaObjectPipe.transformToLink(media, {formatType: 'm3u8_timeline'});
        }
        return MediaObjectPipe.transformToLink(media, {formatType: 'thumb_md', defaultImageType: 'background'});
      }).filter((url): url is string => !!url && !url.startsWith('assets/'));

      this.mediaOffline.prewarmUrls(imageUrls, 'image', this.prewarmWindowPostCount, {
        scope: `${this.mediaPrewarmScope}:images`,
      });
    }
    if (this.viewType !== ViewTypeEnum.GRID) {
      const videoUrls = firstMedia
        .filter((media) => media?.type === MediaTypeEnum.VIDEO)
        .map((media) => MediaObjectPipe.transformToLink(media, {formatType: 'm3u8'}))
        .filter((url): url is string => !!url && !url.startsWith('assets/'));
      this.mediaOffline.prewarmUrls(videoUrls, 'playlist', this.prewarmWindowPostCount, {
        scope: `${this.mediaPrewarmScope}:playlists`,
        cachePlaylistChildren: false,
      });
      return;
    }

    // Media Grid normally needs thumbnails only. Home opts into manifest
    // prewarming for every video tile mounted by the virtual viewport (and
    // its bounded overscan), so any visible video can open promptly. This
    // keeps a short startup buffer on disk but creates no video element,
    // decoder or HLS pipeline here.
    if (this.gridGalleryVideoPrewarmEnabled && !this.isSwipePreview && isPlatformBrowser(this.platformId)) {
      const videoUrls = this.getMountedGridVideoPlaylistUrls();
      if (!videoUrls.length) {
        this.mediaOffline.clearPrewarmScope(`${this.mediaPrewarmScope}:grid-playlists`);
        return;
      }
      this.mediaOffline.prewarmUrls(videoUrls, 'playlist', videoUrls.length, {
        scope: `${this.mediaPrewarmScope}:grid-playlists`,
        cachePlaylistChildren: false,
        startupSegmentCount: 3,
        // Leaving the grid frees its HLS/MSE decoders; retain only this small
        // startup path on disk so a return to the same tile stays fast.
        retainCacheOnScopeRelease: true,
      });
    }
  }

  /**
   * A grid tile represents only its first media item after the optional type
   * filter. Restrict playlist prewarming to those tiles currently mounted by
   * virtualisation; this covers every tappable video without retaining a
   * full-grid manifest queue.
   */
  private getMountedGridVideoPlaylistUrls(): string[] {
    const urls: string[] = [];
    const seenUrls = new Set<string>();

    this.virtualVisibleGridRows.forEach((row) => {
      row.items.forEach(({post}) => {
        const displayMedia = this.visibleMediaType
          ? (post?.media || []).filter((media) => media?.type === this.visibleMediaType)
          : post?.media || [];
        const media = displayMedia[0];
        if (media?.type !== MediaTypeEnum.VIDEO) {
          return;
        }

        const url = MediaObjectPipe.transformToLink(media, {formatType: 'm3u8'});
        if (!url || url.startsWith('assets/') || seenUrls.has(url)) {
          return;
        }

        seenUrls.add(url);
        urls.push(url);
      });
    });

    return urls;
  }

  /**
   * Starts the exact gallery video's shallow manifest request on pointer
   * intent. It does not prevent the gesture, create a player or wait before
   * the modal opens; click repeats it as a keyboard-safe fallback.
   */
  public prewarmGridGalleryVideo(post: PostViewModel): void {
    if (
      this.isSwipePreview
      || !this.gridGalleryVideoPrewarmEnabled
      || !this.mediaPrewarmEnabled
      || this.viewType !== ViewTypeEnum.GRID
      || this.gridTileClickAction !== 'open-gallery'
    ) {
      return;
    }

    const displayMedia = this.visibleMediaType
      ? (post?.media || []).filter((candidate) => candidate?.type === this.visibleMediaType)
      : post?.media || [];
    const media = displayMedia[0];
    if (media?.type !== MediaTypeEnum.VIDEO) {
      return;
    }
    const playlistUrl = media ? MediaObjectPipe.transformToLink(media, {formatType: 'm3u8'}) : null;
    if (!playlistUrl || playlistUrl.startsWith('assets/')) {
      return;
    }

    // Preserve the viewport scope until the gallery is actually open: the
    // exact URL may already have its first startup fragment cached. The
    // service promotes a queued copy of this task instead of restarting it.
    this.mediaOffline.prewarmUrls([playlistUrl], 'playlist', 1, {
      scope: this.gridGalleryIntentPrewarmScope,
      cachePlaylistChildren: false,
      priority: true,
      startupSegmentCount: 4,
      retainCacheOnScopeRelease: true,
    });
    if (this.gridGalleryIntentPrewarmTimeoutId !== null) {
      clearTimeout(this.gridGalleryIntentPrewarmTimeoutId);
    }
    this.gridGalleryIntentPrewarmTimeoutId = setTimeout(() => {
      this.gridGalleryIntentPrewarmTimeoutId = null;
      this.mediaOffline.clearPrewarmScope(this.gridGalleryIntentPrewarmScope);
    }, 10_000);
  }

  private resolvePrewarmWindowStart(loadedPostsCount: number): number {
    const visibleIndexes = this.viewType === ViewTypeEnum.GRID
      ? this.virtualVisibleGridRows.flatMap((row) => row.items.map((item) => item.index))
      : this.virtualVisibleItems.map((item) => item.index);
    const firstVisibleIndex = visibleIndexes.length ? Math.min(...visibleIndexes) : 0;
    return Math.max(0, Math.min(firstVisibleIndex, Math.max(0, loadedPostsCount - this.prewarmWindowPostCount)));
  }

  private clearMediaPrewarm(): void {
    if (this.prewarmTimeoutId !== null) {
      clearTimeout(this.prewarmTimeoutId);
      this.prewarmTimeoutId = null;
    }
    this.mediaOffline.clearPrewarmScope(`${this.mediaPrewarmScope}:images`);
    this.mediaOffline.clearPrewarmScope(`${this.mediaPrewarmScope}:playlists`);
    this.mediaOffline.clearPrewarmScope(`${this.mediaPrewarmScope}:grid-playlists`);
    this.mediaOffline.clearPrewarmScope(this.gridGalleryIntentPrewarmScope);
    this.disposeGridViewportVideoDecoders();
    if (this.gridGalleryIntentPrewarmTimeoutId !== null) {
      clearTimeout(this.gridGalleryIntentPrewarmTimeoutId);
      this.gridGalleryIntentPrewarmTimeoutId = null;
    }
  }

  public canOpenGridItem(post: PostViewModel | null | undefined): boolean {
    if (!post || post.isPlaceholder || !post.id || post.id <= 0) {
      return false;
    }

    if (!post.media?.length) {
      return false;
    }

    return this.getGridThumbnailSource(post) !== 'none';
  }

  public canRenderGridThumbnail(post: PostViewModel | null | undefined): boolean {
    return this.getGridThumbnailSource(post) !== 'none';
  }

  public isGridItemProcessing(post: PostViewModel | null | undefined): boolean {
    if (!post || !post.id || !this.currentUser?.id) {
      return false;
    }

    const ownerId = (post?.postedBy as any)?.id;
    if (ownerId !== this.currentUser.id) {
      return false;
    }

    if (!this.inFlightOwnedPostIds.has(post.id)) {
      return false;
    }

    return this.isInProgressStatus(post.processingStatus) || this.hasInProgressPrimaryMedia(post);
  }

  public getGridThumbnail(post: PostViewModel | null | undefined): string | null {
    if (!post?.id) {
      return null;
    }

    const source = this.getGridThumbnailSource(post);
    if (source === 'local') {
      return this.localGridThumbnailByPostId.get(post.id) || null;
    }

    if (source === 'backend') {
      return this.resolveBackendGridThumbnail(post);
    }

    return null;
  }

  public getGridThumbnailSource(post: PostViewModel | null | undefined): 'local' | 'backend' | 'none' {
    if (!post?.id) {
      return 'none';
    }

    if (this.hasBackendGridThumbnail(post)) {
      return 'backend';
    }

    if (this.localGridThumbnailByPostId.has(post.id)) {
      return 'local';
    }

    return 'none';
  }

  private resolveBackendGridThumbnail(post: PostViewModel): string | null {
    const media = post?.media?.[0];
    if (!media) {
      return null;
    }

    const formatType = media.type === MediaTypeEnum.VIDEO ? 'm3u8_timeline' : 'thumbnail';
    const uri = MediaObjectPipe.transformToLink(media, { formatType, defaultImageType: 'background' });
    if (!uri || uri.startsWith('assets/')) {
      return null;
    }

    return uri;
  }

  private hasBackendGridThumbnail(post: PostViewModel | null | undefined): boolean {
    if (!post?.id || !post.media?.length) {
      return false;
    }

    if (this.backendGridThumbnailReadyByPostId.has(post.id)) {
      return true;
    }

    return !!this.resolveBackendGridThumbnail(post);
  }

  private promoteBackendThumbnailAfterWarmup(post: PostViewModel): void {
    if (!post?.id) {
      return;
    }

    if (this.backendThumbnailPromoteInFlightByPostId.has(post.id)) {
      return;
    }

    const backendUri = this.resolveBackendGridThumbnail(post);
    if (!backendUri) {
      return;
    }

    this.backendThumbnailPromoteInFlightByPostId.add(post.id);
    this.preloadImage(backendUri)
      .finally(() => {
        this.backendGridThumbnailReadyByPostId.add(post.id);
        this.localGridThumbnailByPostId.delete(post.id);
        this.backendThumbnailPromoteInFlightByPostId.delete(post.id);
        this.cdr.detectChanges();
      });
  }

  private preloadImage(url: string): Promise<void> {
    return new Promise((resolve) => {
      if (!isPlatformBrowser(this.platformId) || !url) {
        resolve();
        return;
      }

      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = url;
    });
  }

  private hasInProgressPrimaryMedia(post: PostViewModel): boolean {
    const mediaStatus = (post.media?.[0] as any)?.processingStatus as string | undefined;
    return mediaStatus === 'pending_upload'
      || mediaStatus === 'uploaded'
      || mediaStatus === 'processing';
  }

  private isInProgressStatus(status: string | null | undefined): boolean {
    return status === 'pending' || status === 'processing';
  }

  private isTerminalPostStatus(status: string | null | undefined): boolean {
    return status === 'completed' || status === 'partially_completed' || status === 'failed';
  }

  private isPersistedServerPostId(id?: number | null): id is number {
    return Number.isInteger(id)
      && Number.isSafeInteger(id)
      && (id || 0) > 0
      && (id || 0) <= 2147483647;
  }

  private preview(targetId: string): void {
    if (!isPlatformBrowser(this.platformId) || !targetId) {
      return;
    }
    const element = this.findPreviewElement(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
      this.pendingPreviewTargetId = null;
      this.pendingPreviewIndex = null;
      return;
    }

    const targetIndex = this.resolvePreviewPostIndex(targetId);
    if (targetIndex < 0) {
      return;
    }

    this.pendingPreviewTargetId = targetId;
    this.pendingPreviewIndex = targetIndex;
    this.forceVirtualRenderAroundIndex(targetIndex);

    const targetOffset = this.viewType === ViewTypeEnum.GRID
      ? this.resolveGridOffsetForIndex(targetIndex)
      : this.virtualOffsets[targetIndex] || 0;
    const targetHeight = this.viewType === ViewTypeEnum.GRID
      ? this.virtualGridCellSize || this.virtualFallbackListHeight
      : this.resolveVirtualListHeightForIndex(targetIndex);
    const nextScrollTop = Math.max(
      0,
      this.resolveTimelineViewportTop()
      + targetOffset
      - Math.max(0, (this.getViewportHeight() - targetHeight) / 2)
    );
    window.scrollTo({ top: nextScrollTop, behavior: 'auto' });

    this.runOnNextFrame(() => this.runOnNextFrame(() => {
      const renderedElement = this.findPreviewElement(targetId);
      if (renderedElement) {
        renderedElement.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
      }
      this.pendingPreviewTargetId = null;
      this.pendingPreviewIndex = null;
      this.scheduleVirtualLayoutSync();
    }));
  }

  private findPreviewElement(targetId: string): HTMLElement | null {
    return this.document.querySelector(`#${targetId}`) as HTMLElement | null;
  }

  private resolvePreviewPostIndex(targetId: string): number {
    const postId = Number(targetId.replace(/^item/, ''));
    if (!Number.isFinite(postId)) {
      return -1;
    }

    return this.posts.findIndex((post) => post?.id === postId);
  }

  private forceVirtualRenderAroundIndex(index: number): void {
    if (index < 0 || index >= this.posts.length) {
      return;
    }

    if (this.viewType === ViewTypeEnum.GRID) {
      this.updateVirtualGridMetrics();
      const columns = this.resolveEffectiveGridColumns();
      const rowCount = Math.ceil(this.posts.length / columns);
      const rowIndex = Math.floor(index / columns);
      this.applyVirtualGridSlice(
        Math.max(0, rowIndex - 1),
        Math.min(rowCount, rowIndex + 3),
        rowCount
      );
      this.cdr.detectChanges();
      return;
    }

    this.rebuildVirtualOffsets();
    const startIndex = Math.max(0, index - 2);
    const endIndex = Math.min(this.posts.length, Math.max(index + 3, startIndex + this.virtualMinListRenderCount));
    this.applyVirtualListSlice(startIndex, endIndex);
    this.cdr.detectChanges();
  }

  private resolveGridOffsetForIndex(index: number): number {
    return Math.floor(index / this.resolveEffectiveGridColumns()) * this.virtualGridRowStride;
  }

  private scheduleVirtualLayoutSync(): void {
    if (this.isSwipePreview) {
      this.renderSwipePreviewWindow();
      return;
    }
    if (this.virtualLayoutScheduled) {
      return;
    }

    this.virtualLayoutScheduled = true;
    this.runOnNextFrame(() => {
      this.virtualLayoutScheduled = false;
      if (this.isSwipePreview) {
        this.renderSwipePreviewWindow();
        return;
      }
      this.updateVirtualViewport();
      this.syncVirtualItemObservers();
      this.syncAppendSentinelObserver();
      this.cdr.detectChanges();
      this.schedulePrewarmMedia([], 'viewport-change');
      this.maybeRequestNextPostsPageForViewportProximity();
    });
  }

  private updateVirtualViewport(): void {
    if (this.viewType === ViewTypeEnum.GRID) {
      this.updateVirtualGridViewport();
      return;
    }

    this.updateVirtualListViewport();
  }

  private updateVirtualListViewport(): void {
    this.disposeGridViewportVideoDecoders();
    if (!this.posts?.length) {
      this.virtualVisibleItems = [];
      this.virtualTopSpacerHeight = 0;
      this.virtualBottomSpacerHeight = 0;
      return;
    }

    this.rebuildVirtualOffsets();

    if (!isPlatformBrowser(this.platformId) || !this.timelineViewport?.nativeElement) {
      this.applyVirtualListSlice(0, this.posts.length);
      return;
    }

    const viewportTop = this.getViewportScrollTop() - this.resolveTimelineViewportTop();
    const viewportBottom = viewportTop + this.getViewportHeight();
    const overscan = this.getVirtualListOverscanPx();
    let startIndex = this.findListStartIndex(Math.max(0, viewportTop - overscan));
    let endIndex = this.findListEndIndex(viewportBottom + overscan);

    if (this.pendingPreviewIndex !== null && this.pendingPreviewIndex >= 0) {
      startIndex = Math.min(startIndex, Math.max(0, this.pendingPreviewIndex - 2));
      endIndex = Math.max(endIndex, Math.min(this.posts.length, this.pendingPreviewIndex + 3));
    }

    if ((endIndex - startIndex) < this.virtualMinListRenderCount) {
      endIndex = Math.min(this.posts.length, startIndex + this.virtualMinListRenderCount);
    }

    this.applyVirtualListSlice(startIndex, endIndex);
  }

  private updateVirtualGridViewport(): void {
    if (!this.posts?.length) {
      this.disposeGridViewportVideoDecoders();
      this.virtualVisibleGridRows = [];
      this.virtualTopSpacerHeight = 0;
      this.virtualBottomSpacerHeight = 0;
      return;
    }

    this.updateVirtualGridMetrics();
    const columns = this.resolveEffectiveGridColumns();
    const rowCount = Math.ceil(this.posts.length / columns);

    if (!isPlatformBrowser(this.platformId) || !this.timelineViewport?.nativeElement || !this.virtualGridRowStride) {
      this.applyVirtualGridSlice(0, rowCount, rowCount);
      return;
    }

    const viewportTop = this.getViewportScrollTop() - this.resolveTimelineViewportTop();
    const viewportBottom = viewportTop + this.getViewportHeight();
    const overscan = Math.max(this.getViewportHeight() * 1.5, this.virtualGridRowStride * 3);
    let startRow = Math.max(0, Math.floor(Math.max(0, viewportTop - overscan) / this.virtualGridRowStride));
    let endRow = Math.min(rowCount, Math.ceil((viewportBottom + overscan) / this.virtualGridRowStride));

    if (this.pendingPreviewIndex !== null && this.pendingPreviewIndex >= 0) {
      const previewRow = Math.floor(this.pendingPreviewIndex / columns);
      startRow = Math.min(startRow, Math.max(0, previewRow - 1));
      endRow = Math.max(endRow, Math.min(rowCount, previewRow + 3));
    }

    this.applyVirtualGridSlice(startRow, endRow, rowCount);
  }

  private applyVirtualListSlice(startIndex: number, endIndex: number): void {
    const safeStart = Math.max(0, Math.min(startIndex, this.posts.length));
    const safeEnd = Math.max(safeStart, Math.min(endIndex, this.posts.length));
    const totalHeight = this.virtualOffsets[this.posts.length] || 0;

    this.virtualVisibleItems = this.posts.slice(safeStart, safeEnd).map((post, offset) => ({
      index: safeStart + offset,
      key: this.getVirtualPostKey(post, safeStart + offset),
      post,
    }));
    this.virtualTopSpacerHeight = this.virtualOffsets[safeStart] || 0;
    this.virtualBottomSpacerHeight = Math.max(0, totalHeight - (this.virtualOffsets[safeEnd] || 0));
  }

  private applyVirtualGridSlice(startRow: number, endRow: number, rowCount: number): void {
    const safeStart = Math.max(0, Math.min(startRow, rowCount));
    const safeEnd = Math.max(safeStart, Math.min(endRow, rowCount));
    const rows: TimelineVirtualGridRow[] = [];

    for (let rowIndex = safeStart; rowIndex < safeEnd; rowIndex++) {
      const columns = this.resolveEffectiveGridColumns();
      const startIndex = rowIndex * columns;
      const items = this.posts.slice(startIndex, startIndex + columns).map((post, offset) => ({
        index: startIndex + offset,
        key: this.getVirtualPostKey(post, startIndex + offset),
        post,
      }));

      rows.push({
        items,
        key: `row:${rowIndex}`,
        rowIndex,
      });
    }

    this.virtualVisibleGridRows = rows;
    this.virtualTopSpacerHeight = safeStart * this.virtualGridRowStride;
    this.virtualBottomSpacerHeight = Math.max(0, (rowCount - safeEnd) * this.virtualGridRowStride);
    this.syncGridViewportVideoDecoders();
  }

  /**
   * The virtual slice has an overscan buffer for smooth scrolling. Decoder
   * lifetime is deliberately narrower: only rows intersecting the browser
   * viewport can own a warm HLS pipeline. On every slice update the service
   * synchronously disposes rows that left that real window.
   */
  private syncGridViewportVideoDecoders(): void {
    if (
      this.isSwipePreview
      || !this.gridGalleryVideoPrewarmEnabled
      || this.viewType !== ViewTypeEnum.GRID
      || !isPlatformBrowser(this.platformId)
      || !this.virtualGridRowStride
      || !this.timelineViewport?.nativeElement
    ) {
      this.disposeGridViewportVideoDecoders();
      return;
    }

    const viewportTop = Math.max(0, this.getViewportScrollTop() - this.resolveTimelineViewportTop());
    const viewportBottom = viewportTop + this.getViewportHeight();
    const rowStride = this.virtualGridRowStride || 1;
    const firstVisibleRow = Math.max(0, Math.floor(viewportTop / rowStride));
    const lastVisibleRow = Math.ceil(viewportBottom / rowStride);
    const videos: MediaObjectInterface[] = [];
    const seenMediaIds = new Set<string | number>();

    this.virtualVisibleGridRows
      .filter((row) => row.rowIndex >= firstVisibleRow && row.rowIndex < lastVisibleRow)
      .forEach((row) => row.items.forEach(({post}) => {
        const displayMedia = this.visibleMediaType
          ? (post?.media || []).filter((media) => media?.type === this.visibleMediaType)
          : post?.media || [];
        const media = displayMedia[0];
        if (media?.type !== MediaTypeEnum.VIDEO || !media.id || seenMediaIds.has(media.id)) {
          return;
        }
        seenMediaIds.add(media.id);
        videos.push(media);
      }));

    // This runs in the same viewport pass that creates the hidden HLS
    // decoder, rather than waiting for the debounced generic media prewarm.
    // It gives every actually tappable tile its master playlist, level-0
    // variant, init data and first three fragments before a click can open
    // the modal.
    const playlistUrls = videos
      .map((media) => MediaObjectPipe.transformToLink(media, {formatType: 'm3u8'}))
      .filter((url): url is string => !!url && !url.startsWith('assets/'));
    this.mediaOffline.prewarmUrls(playlistUrls, 'playlist', playlistUrls.length, {
      scope: `${this.mediaPrewarmScope}:grid-playlists`,
      cachePlaylistChildren: false,
      // A real, tappable viewport must not wait behind thumbnail/background
      // work from the rest of the timeline. The queue retains the visual
      // order so the first tiles visible to the user warm first.
      priority: true,
      startupSegmentCount: 3,
      retainCacheOnScopeRelease: true,
    });

    const reconcile = (this.videoService as VideoService & {
      reconcileViewportWarmDecoders?: (scope: string, host: HTMLElement, media: MediaObjectInterface[]) => void;
    }).reconcileViewportWarmDecoders;
    reconcile?.call(this.videoService,
      this.gridViewportDecoderScope,
      this.timelineViewport.nativeElement,
      videos,
    );
  }

  private disposeGridViewportVideoDecoders(): void {
    (this.videoService as VideoService & {disposeViewportWarmScope?: (scope: string) => void})
      .disposeViewportWarmScope?.(this.gridViewportDecoderScope);
  }

  private getVirtualPostKey(post: PostViewModel | undefined, index: number): string {
    if (post?.id) {
      return `post:${post.id}`;
    }

    if (this.isAppendPlaceholder(post)) {
      return `append-placeholder:${index}`;
    }

    if (post?.isPlaceholder) {
      return `placeholder:${index}`;
    }

    return `post-index:${index}`;
  }

  private updateVirtualGridMetrics(): void {
    const columns = this.resolveEffectiveGridColumns();
    this.effectiveGridColumns = columns;
    if (!isPlatformBrowser(this.platformId)) {
      this.virtualGridCellSize = 0;
      this.virtualGridRowStride = 0;
      return;
    }

    const viewportWidth = this.timelineViewport?.nativeElement?.clientWidth
      || this.document.documentElement?.clientWidth
      || window.innerWidth
      || 0;
    const contentWidth = Math.max(0, viewportWidth - (this.virtualGridGapPx * (columns - 1)));
    this.virtualGridCellSize = contentWidth > 0 ? contentWidth / columns : 0;
    this.virtualGridRowStride = this.virtualGridCellSize > 0 ? this.virtualGridCellSize + this.virtualGridGapPx : 0;
  }

  private resolveEffectiveGridColumns(): number {
    const config = typeof this._gridColumns === 'number'
      ? {default: this._gridColumns}
      : this._gridColumns;
    const defaultColumns = Math.max(1, config.default || 3);
    if (!isPlatformBrowser(this.platformId)) {
      return defaultColumns;
    }

    const width = window.innerWidth || 0;
    if (config.desktop && width >= 1200) {
      return Math.max(1, config.desktop);
    }
    if (config.tablet && width >= 768) {
      return Math.max(1, config.tablet);
    }
    return defaultColumns;
  }

  private rebuildVirtualOffsets(): void {
    const offsets = new Array(this.posts.length + 1);
    offsets[0] = 0;

    for (let index = 0; index < this.posts.length; index++) {
      offsets[index + 1] = offsets[index] + this.resolveVirtualListHeightForIndex(index);
    }

    this.virtualOffsets = offsets;
  }

  private resolveVirtualListHeightForIndex(index: number): number {
    const key = this.getVirtualPostKey(this.posts[index], index);
    return this.virtualListItemHeights.get(key) || this.virtualAverageListItemHeight || this.virtualFallbackListHeight;
  }

  private findListStartIndex(offset: number): number {
    let low = 0;
    let high = this.posts.length;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if ((this.virtualOffsets[mid + 1] || 0) <= offset) {
        low = mid + 1;
        continue;
      }

      high = mid;
    }

    return low;
  }

  private findListEndIndex(offset: number): number {
    let low = 0;
    let high = this.posts.length;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      if ((this.virtualOffsets[mid] || 0) < offset) {
        low = mid + 1;
        continue;
      }

      high = mid;
    }

    return Math.max(1, Math.min(this.posts.length, low));
  }

  private getVirtualListOverscanPx(): number {
    return Math.max(1200, this.getViewportHeight() * 2);
  }

  private resolveTimelineViewportTop(): number {
    if (!isPlatformBrowser(this.platformId) || !this.timelineViewport?.nativeElement) {
      return 0;
    }

    const rect = this.timelineViewport.nativeElement.getBoundingClientRect();
    return rect.top + this.getViewportScrollTop();
  }

  private getViewportHeight(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 0;
    }

    return window.innerHeight || this.document.documentElement?.clientHeight || 0;
  }

  private getViewportScrollTop(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 0;
    }

    return window.scrollY || window.pageYOffset || this.document.documentElement?.scrollTop || 0;
  }

  private syncVirtualItemObservers(): void {
    if (this.isSwipePreview || !isPlatformBrowser(this.platformId) || this.viewType !== ViewTypeEnum.LIST) {
      this.disconnectVirtualResizeObserver();
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    if (!this.virtualResizeObserver) {
      this.virtualResizeObserver = new ResizeObserver((entries) => {
        let changed = false;

        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;
          const key = target.dataset['virtualKey'];
          if (!key) {
            return;
          }

          changed = this.captureVirtualItemHeight(key, target.getBoundingClientRect().height) || changed;
        });

        if (!changed) {
          return;
        }

        this.virtualAverageListItemHeight = this.computeAverageVirtualItemHeight();
        this.scheduleVirtualLayoutSync();
      });
    }

    const nextObserved = new Map<string, HTMLElement>();
    (this.virtualItemRefs?.toArray() || []).forEach((ref) => {
      const element = ref.nativeElement;
      const key = element.dataset['virtualKey'];
      if (!key) {
        return;
      }

      nextObserved.set(key, element);
      if (this.observedVirtualElements.get(key) !== element) {
        this.virtualResizeObserver?.observe(element);
      }
      this.captureVirtualItemHeight(key, element.getBoundingClientRect().height);
    });

    this.observedVirtualElements.forEach((element, key) => {
      if (nextObserved.get(key) === element) {
        return;
      }
      this.virtualResizeObserver?.unobserve(element);
    });

    this.observedVirtualElements.clear();
    nextObserved.forEach((element, key) => this.observedVirtualElements.set(key, element));
    this.virtualAverageListItemHeight = this.computeAverageVirtualItemHeight();
  }

  private captureVirtualItemHeight(key: string, nextHeight: number): boolean {
    const normalizedHeight = Math.max(1, Math.ceil(nextHeight));
    const previousHeight = this.virtualListItemHeights.get(key) || 0;
    if (Math.abs(previousHeight - normalizedHeight) < 2) {
      return false;
    }

    this.virtualListItemHeights.set(key, normalizedHeight);
    return true;
  }

  private computeAverageVirtualItemHeight(): number {
    const measuredHeights = Array.from(this.virtualListItemHeights.values()).filter((height) => height > 0);
    if (!measuredHeights.length) {
      return this.virtualFallbackListHeight;
    }

    const total = measuredHeights.reduce((sum, height) => sum + height, 0);
    return Math.max(240, Math.round(total / measuredHeights.length));
  }

  private syncAppendSentinelObserver(): void {
    if (this.isSwipePreview || !isPlatformBrowser(this.platformId) || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const sentinel = this.appendSentinel?.nativeElement;
    if (!sentinel) {
      return;
    }

    if (!this.virtualAppendObserver) {
      this.virtualAppendObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }

        this.maybeRequestNextPostsPageForViewportProximity();
      }, {
        root: null,
        rootMargin: `${this.appendTriggerRootMarginPx}px 0px ${this.appendTriggerRootMarginPx}px 0px`,
        threshold: 0,
      });
    }

    if (this.observedAppendSentinel === sentinel) {
      return;
    }

    if (this.observedAppendSentinel) {
      this.virtualAppendObserver.unobserve(this.observedAppendSentinel);
    }

    this.observedAppendSentinel = sentinel;
    this.virtualAppendObserver.observe(sentinel);
  }

  private maybeRequestNextPostsPageForViewportProximity(): void {
    if (this.isSwipePreview || !isPlatformBrowser(this.platformId) || !this.prefetchEnabled) {
      return;
    }

    const sentinel = this.appendSentinel?.nativeElement;
    if (!sentinel) {
      return;
    }

    const rect = sentinel.getBoundingClientRect();
    const viewportHeight = this.getViewportHeight();
    const withinAppendZone = rect.top <= viewportHeight + this.appendTriggerRootMarginPx
      && rect.bottom >= -this.appendTriggerRootMarginPx;

    if (!withinAppendZone) {
      return;
    }

    this.maybeRequestNextPostsPage();
  }

  private disconnectVirtualResizeObserver(): void {
    this.virtualResizeObserver?.disconnect();
    this.virtualResizeObserver = null;
    this.observedVirtualElements.clear();
  }

  private disconnectVirtualObservers(): void {
    this.disconnectVirtualResizeObserver();
    this.virtualAppendObserver?.disconnect();
    this.virtualAppendObserver = null;
    this.observedAppendSentinel = null;
  }

  private schedulePrewarmMedia(posts: PostViewModel[], reason: string): void {
    if (this.isSwipePreview || !this.prefetchEnabled || !this.mediaPrewarmEnabled || !this.posts?.length) {
      return;
    }
    if (this.prewarmTimeoutId !== null) {
      clearTimeout(this.prewarmTimeoutId);
    }
    this.prewarmTimeoutId = setTimeout(() => {
      this.prewarmTimeoutId = null;
      this.prewarmMedia(posts, reason);
    }, 180);
  }

  private scheduleGridSync(
    reason: string,
    interaction: string,
    options?: { startedAt?: number; onComplete?: () => void }
  ): void {
    if (this.isSwipePreview) {
      this.renderSwipePreviewWindow();
      options?.onComplete?.();
      return;
    }
    this.pendingGridSyncReasons.push(reason);
    this.pendingGridSyncInteraction = this.pendingGridSyncInteraction || interaction;
    this.pendingGridSyncStartAt = this.pendingGridSyncStartAt ?? options?.startedAt ?? this.now();
    if (options?.onComplete) {
      this.pendingGridSyncCallbacks.push(options.onComplete);
    }

    if (this.gridSyncScheduled) {
      return;
    }

    this.gridSyncScheduled = true;
    this.runOnNextFrame(() => this.runOnNextFrame(() => {
      this.gridSyncScheduled = false;
      const callbacks = [...this.pendingGridSyncCallbacks];

      this.pendingGridSyncReasons = [];
      this.pendingGridSyncCallbacks = [];
      this.pendingGridSyncStartAt = null;
      this.pendingGridSyncInteraction = null;

      if (this.isSwipePreview) {
        this.renderSwipePreviewWindow();
        callbacks.forEach((callback) => callback());
        return;
      }

      this.updateVirtualViewport();
      this.syncVirtualItemObservers();
      this.syncAppendSentinelObserver();
      this.cdr.detectChanges();
      this.schedulePrewarmMedia([], 'viewport-change');
      this.maybeRequestNextPostsPageForViewportProximity();

      callbacks.forEach((callback) => callback());
    }));
  }

  private registerPerformanceDiagnostics(): void {
    if (this.isSwipePreview || !isPlatformBrowser(this.platformId) || typeof PerformanceObserver === 'undefined') {
      return;
    }

    try {
      this.performanceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: any) => {
          if (entry?.entryType !== 'layout-shift' || entry?.hadRecentInput) {
            return;
          }
          this.layoutShiftCount += 1;
          this.layoutShiftTotal += Number(entry?.value || 0);
        });
      });
      this.performanceObserver.observe({type: 'layout-shift', buffered: true} as PerformanceObserverInit);
    } catch {
      this.performanceObserver = null;
    }
  }

  private runOnNextFrame(callback: () => void): void {
    if (!isPlatformBrowser(this.platformId)) {
      callback();
      return;
    }

    const frameId = requestAnimationFrame(() => {
      const index = this.animationFrameIds.indexOf(frameId);
      if (index >= 0) {
        this.animationFrameIds.splice(index, 1);
      }
      callback();
    });
    this.animationFrameIds.push(frameId);
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  private hasPlaceholderPosts(): boolean {
    return this.posts?.some((post) => post?.isPlaceholder && !this.isAppendPlaceholder(post));
  }

  private emitLoadedPosts(): void {
    this.loadedPosts = this.getLoadedPosts();
    this.loadedPosts$.next(this.loadedPosts);
    this.loadedPostsChange.emit(this.loadedPosts);
    this.scheduleTimelineStateEmit();
  }

  private getLoadedPosts(): PostViewModel[] {
    return (this.posts || []).filter((post) =>
      !!post?.id && !post.isPlaceholder && !this.isAppendPlaceholder(post)
    );
  }

  private scheduleTimelineStateEmit(): void {
    if (this.isSwipePreview || this.singlePostMode || !this.timelineStateChange.observed || this.stateSnapshotTimeoutId !== null) {
      return;
    }
    this.stateSnapshotTimeoutId = setTimeout(() => {
      this.stateSnapshotTimeoutId = null;
      this.emitTimelineState();
    }, 150);
  }

  private scheduleTimelineScrollEmit(): void {
    if (this.isSwipePreview || this.singlePostMode || !this.timelineScrollChange.observed || this.scrollStateTimeoutId !== null) {
      return;
    }
    this.scrollStateTimeoutId = setTimeout(() => {
      this.scrollStateTimeoutId = null;
      if (!this.isSwipePreview && !this.singlePostMode && this.timelineScrollChange.observed) {
        this.timelineScrollChange.emit(this.getScrollY());
      }
    }, 150);
  }

  private emitTimelineState(): void {
    if (this.isSwipePreview || this.singlePostMode || !this.timelineStateChange.observed) {
      return;
    }
    this.timelineStateChange.emit(this.captureState());
  }

}
